import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Link,
  Alert,
  Paper,
  FormControlLabel,
  Autocomplete,
  LinearProgress,
  Switch,
  AlertTitle,
  InputAdornment,
  Stack,
  Chip as MuiChip,
} from '@mui/material';
import { StepIndicator, Step as StepType } from './StepIndicator';
import { Info, ListChecks, Zap, Settings, CheckCircle } from 'lucide-react';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Settings as SettingsIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Business as BusinessIcon,
  Engineering as EngineeringIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  NetworkCheck as NetworkIcon,
  Speed as SpeedIcon,
  Code as CodeIcon,
  DataObject as DataObjectIcon,
  Description as TemplateIcon,
  Label as LabelIcon,
  AutoFixHigh as AutoFixHighIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
  HourglassEmpty as HourglassEmptyIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  CalendarMonth as CalendarMonthIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { addDays, addMonths, endOfMonth, endOfWeek, format, isSameDay, isSameMonth, startOfMonth, startOfWeek, subMonths } from 'date-fns';
import { TagRule, RuleCondition, TagTemplate, ServiceConfig } from '../types/tagops';
import { tagopsService } from '../services/tagopsService';

const ALLOW_UNKNOWN_CREATION_TIME_FLAG = 'allow_unknown_creation_time';

function parseCreationEpochValue(value: RuleCondition['value']): number {
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'string' && /^\d+$/.test(value)) return parseInt(value, 10);
  return NaN;
}

function buildCalendarDays(month: Date): Date[] {
  const days: Date[] = [];
  let day = startOfWeek(startOfMonth(month), { weekStartsOn: 0 });
  const end = endOfWeek(endOfMonth(month), { weekStartsOn: 0 });
  while (day <= end) {
    days.push(day);
    day = addDays(day, 1);
  }
  return days;
}

// Professional rule categories for different teams
const RULE_CATEGORIES = {
  'cost-management': {
    name: 'Cost Management',
    icon: <BusinessIcon />,
    color: '#2e7d32',
    description: 'Rules for cost allocation and optimization',
    tags: ['cost-center', 'budget', 'billing', 'finops'],
    examples: [
      'Automatically tag resources with cost center',
      'Apply budget allocation tags',
      'Tag resources for billing purposes',
    ],
  },
  'security-compliance': {
    name: 'Security & Compliance',
    icon: <SecurityIcon />,
    color: '#d32f2f',
    description: 'Rules for security and compliance requirements',
    tags: ['security', 'compliance', 'audit', 'governance'],
    examples: [
      'Tag resources with security classification',
      'Apply compliance framework tags',
      'Mark resources for audit purposes',
    ],
  },
  'operations': {
    name: 'Operations',
    icon: <EngineeringIcon />,
    color: '#1976d2',
    description: 'Rules for operational management',
    tags: ['operations', 'maintenance', 'monitoring', 'devops'],
    examples: [
      'Tag resources with team ownership',
      'Apply maintenance window tags',
      'Mark resources for monitoring',
    ],
  },
  'development': {
    name: 'Development',
    icon: <CodeIcon />,
    color: '#ed6c02',
    description: 'Rules for development and deployment',
    tags: ['development', 'deployment', 'environment', 'ci-cd'],
    examples: [
      'Tag resources by environment',
      'Apply deployment stage tags',
      'Mark resources for CI/CD',
    ],
  },
  'data-governance': {
    name: 'Data Governance',
    icon: <DataObjectIcon />,
    color: '#7b1fa2',
    description: 'Rules for data classification and governance',
    tags: ['data', 'classification', 'privacy', 'retention'],
    examples: [
      'Tag resources with data classification',
      'Apply privacy level tags',
      'Mark resources for retention policies',
    ],
  },
  'network': {
    name: 'Network',
    icon: <NetworkIcon />,
    color: '#1565c0',
    description: 'Rules for network resources',
    tags: ['network', 'vpc', 'subnet', 'security-group'],
    examples: [
      'Tag network resources by purpose',
      'Apply VPC and subnet tags',
      'Mark security groups by function',
    ],
  },
  'storage': {
    name: 'Storage',
    icon: <StorageIcon />,
    color: '#388e3c',
    description: 'Rules for storage resources',
    tags: ['storage', 's3', 'ebs', 'backup'],
    examples: [
      'Tag storage by type and purpose',
      'Apply backup policy tags',
      'Mark storage for lifecycle management',
    ],
  },
  'compute': {
    name: 'Compute',
    icon: <SpeedIcon />,
    color: '#f57c00',
    description: 'Rules for compute resources',
    tags: ['compute', 'ec2', 'lambda', 'container'],
    examples: [
      'Tag compute resources by workload',
      'Apply instance type tags',
      'Mark containers by service',
    ],
  },
};

// Blacklisted tag keys that are reserved for AWS internal use
const BLACKLISTED_TAG_KEYS = [
  'aws:',
  'CreatedBy',
  'EC2ImageBuilderArn',
  'awsApplication',
  'ImageLifecycle',
  'aws-cloudformation*',
  'elasticbeanstalk:',
  'kubernetes.io/',
  'k8s.io/',
  'aws-cdk:',
  'aws-amplify:',
  'aws-copilot:'
];

// AWS Tagging Restrictions based on https://docs.aws.amazon.com/emr/latest/EMR-Serverless-UserGuide/tagging-restrictions.html
const AWS_TAGGING_RESTRICTIONS = {
  MAX_TAGS_PER_RESOURCE: 50,
  MAX_KEY_LENGTH: 128,
  MAX_VALUE_LENGTH: 256,
  ALLOWED_CHARACTERS: /^[a-zA-Z0-9\s._:/=+\-@]*$/,
  RESERVED_PREFIXES: ['aws:', 'AWS:'],
  MAX_TAGS_PER_RESOURCE_MESSAGE: 'Maximum 50 user-created tags per resource',
  MAX_KEY_LENGTH_MESSAGE: 'Tag key cannot exceed 128 Unicode characters',
  MAX_VALUE_LENGTH_MESSAGE: 'Tag value cannot exceed 256 Unicode characters',
  INVALID_CHARACTERS_MESSAGE: 'Tag contains invalid characters. Only letters, numbers, spaces, and . : / = + - @ are allowed',
  RESERVED_PREFIX_MESSAGE: 'Tag key cannot start with "aws:" (case-insensitive) as it is reserved for AWS use',
  EMPTY_KEY_MESSAGE: 'Tag key cannot be an empty string',
  NULL_VALUE_MESSAGE: 'Tag value cannot be null'
};

// Function to check if a tag key is blacklisted
const isTagKeyBlacklisted = (tagKey: string): boolean => {
  return BLACKLISTED_TAG_KEYS.some(blacklistedKey => {
    if (blacklistedKey.endsWith('*')) {
      // Handle wildcard patterns (e.g., "aws-cloudformation*")
      const pattern = blacklistedKey.slice(0, -1);
      return tagKey.startsWith(pattern);
    } else if (blacklistedKey.endsWith(':')) {
      // Handle prefix patterns (e.g., "aws:", "elasticbeanstalk:")
      return tagKey.startsWith(blacklistedKey);
    } else {
      // Handle exact matches
      return tagKey === blacklistedKey;
    }
  });
};

// Comprehensive AWS tagging validation function
const validateAWSTag = (tagKey: string, tagValue: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Check for empty key
  if (tagKey.trim() === '') {
    errors.push(AWS_TAGGING_RESTRICTIONS.EMPTY_KEY_MESSAGE);
  }

  // Check for null value (empty string is allowed, but null is not)
  if (tagValue === null || tagValue === undefined) {
    errors.push(AWS_TAGGING_RESTRICTIONS.NULL_VALUE_MESSAGE);
  }

  // Check key length
  if (tagKey.length > AWS_TAGGING_RESTRICTIONS.MAX_KEY_LENGTH) {
    errors.push(AWS_TAGGING_RESTRICTIONS.MAX_KEY_LENGTH_MESSAGE);
  }

  // Check value length
  if (tagValue && tagValue.length > AWS_TAGGING_RESTRICTIONS.MAX_VALUE_LENGTH) {
    errors.push(AWS_TAGGING_RESTRICTIONS.MAX_VALUE_LENGTH_MESSAGE);
  }

  // Check for reserved AWS prefixes (case-insensitive)
  const lowerTagKey = tagKey.toLowerCase();
  if (AWS_TAGGING_RESTRICTIONS.RESERVED_PREFIXES.some(prefix => 
    lowerTagKey.startsWith(prefix.toLowerCase())
  )) {
    errors.push(AWS_TAGGING_RESTRICTIONS.RESERVED_PREFIX_MESSAGE);
  }

  // Check for blacklisted keys
  if (isTagKeyBlacklisted(tagKey)) {
    errors.push(`Tag key "${tagKey}" is reserved for AWS internal use`);
  }

  // Check for invalid characters in key
  if (tagKey && !AWS_TAGGING_RESTRICTIONS.ALLOWED_CHARACTERS.test(tagKey)) {
    errors.push(AWS_TAGGING_RESTRICTIONS.INVALID_CHARACTERS_MESSAGE);
  }

  // Check for invalid characters in value
  if (tagValue && !AWS_TAGGING_RESTRICTIONS.ALLOWED_CHARACTERS.test(tagValue)) {
    errors.push(AWS_TAGGING_RESTRICTIONS.INVALID_CHARACTERS_MESSAGE);
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Professional rule templates for quick setup
const PROFESSIONAL_TEMPLATES = {
  // COST MANAGEMENT TEMPLATES
  'cost-center': {
    name: 'Cost Center Template',
    description: 'Standard cost allocation tags',
    category: 'cost-management',
    icon: <BusinessIcon />,
    color: '#2e7d32',
    tags: [
      { key: 'CostCenter', value: '' },
      { key: 'Project', value: '' },
      { key: 'Department', value: '' },
      { key: 'BudgetOwner', value: '' },
    ],
  },
  'business-unit': {
    name: 'Business Unit Template',
    description: 'Business unit cost allocation for financial reporting',
    category: 'cost-management',
    icon: <BusinessIcon />,
    color: '#2e7d32',
    tags: [
      { key: 'BusinessUnit', value: '' },
      { key: 'CostCenter', value: '' },
      { key: 'ProfitCenter', value: '' },
      { key: 'FinanceOwner', value: '' },
      { key: 'BudgetCode', value: '' },
    ],
  },
  'customer-billing': {
    name: 'Customer Billing Template',
    description: 'Customer-specific cost allocation for billing',
    category: 'cost-management',
    icon: <BusinessIcon />,
    color: '#2e7d32',
    tags: [
      { key: 'Customer', value: '' },
      { key: 'CustomerType', value: '' },
      { key: 'BillingCode', value: '' },
      { key: 'RevenueStream', value: '' },
      { key: 'ContractId', value: '' },
    ],
  },
  'project-cost-tracking': {
    name: 'Project Cost Tracking Template',
    description: 'Project-based cost tracking and allocation',
    category: 'cost-management',
    icon: <BusinessIcon />,
    color: '#2e7d32',
    tags: [
      { key: 'Project', value: '' },
      { key: 'ProjectPhase', value: '' },
      { key: 'ProjectManager', value: '' },
      { key: 'ProjectBudget', value: '' },
      { key: 'ProjectCode', value: '' },
    ],
  },
  'resource-optimization': {
    name: 'Resource Optimization Template',
    description: 'Tags for cost optimization and resource management',
    category: 'cost-management',
    icon: <BusinessIcon />,
    color: '#2e7d32',
    tags: [
      { key: 'CostOptimization', value: 'enabled' },
      { key: 'AutoShutdown', value: 'enabled' },
      { key: 'ResourceTier', value: 'standard' },
      { key: 'OptimizationPriority', value: 'medium' },
    ],
  },

  // SECURITY & ABAC TEMPLATES
  'security-classification': {
    name: 'Security Classification Template',
    description: 'Data security classification tags',
    category: 'security-compliance',
    icon: <SecurityIcon />,
    color: '#d32f2f',
    tags: [
      { key: 'DataClassification', value: 'Internal' },
      { key: 'SecurityLevel', value: 'Standard' },
      { key: 'ComplianceFramework', value: '' },
      { key: 'DataRetention', value: '7years' },
    ],
  },
  'abac-access-control': {
    name: 'ABAC Access Control Template',
    description: 'Attribute-based access control tags for IAM policies',
    category: 'security-compliance',
    icon: <SecurityIcon />,
    color: '#d32f2f',
    tags: [
      { key: 'AccessLevel', value: 'standard' },
      { key: 'DataSensitivity', value: 'internal' },
      { key: 'ComplianceRequirement', value: '' },
      { key: 'AccessRole', value: '' },
      { key: 'SecurityZone', value: 'standard' },
    ],
  },
  'data-privacy': {
    name: 'Data Privacy Template',
    description: 'Data privacy and protection classification',
    category: 'security-compliance',
    icon: <SecurityIcon />,
    color: '#d32f2f',
    tags: [
      { key: 'DataPrivacy', value: 'standard' },
      { key: 'PIILevel', value: 'none' },
      { key: 'DataRetention', value: '7years' },
      { key: 'EncryptionRequired', value: 'yes' },
      { key: 'AuditLevel', value: 'standard' },
    ],
  },
  'compliance-framework': {
    name: 'Compliance Framework Template',
    description: 'Compliance framework and regulatory requirements',
    category: 'security-compliance',
    icon: <SecurityIcon />,
    color: '#d32f2f',
    tags: [
      { key: 'ComplianceFramework', value: '' },
      { key: 'RegulatoryRequirement', value: '' },
      { key: 'AuditScope', value: 'included' },
      { key: 'ComplianceOwner', value: '' },
      { key: 'ReviewCycle', value: 'quarterly' },
    ],
  },
  'security-baseline': {
    name: 'Security Baseline Template',
    description: 'Security baseline and hardening requirements',
    category: 'security-compliance',
    icon: <SecurityIcon />,
    color: '#d32f2f',
    tags: [
      { key: 'SecurityBaseline', value: 'standard' },
      { key: 'HardeningLevel', value: 'standard' },
      { key: 'SecurityScanning', value: 'enabled' },
      { key: 'VulnerabilityManagement', value: 'enabled' },
    ],
  },
  'aws-inspector-cis': {
    name: 'AWS Inspector CIS Template',
    description: 'Template for performing CIS scans on EC2 instances using AWS Inspector',
    category: 'security-compliance',
    icon: <SecurityIcon />,
    color: '#d32f2f',
    tags: [
      { key: 'cis-scan', value: 'true' },
    ],
  },
  'aws-patch-manager': {
    name: 'AWS Patch Manager Template',
    description: 'Template for organizing EC2 instances into patch groups for automated patching operations',
    category: 'operations',
    icon: <EngineeringIcon />,
    color: '#1976d2',
    tags: [
      { key: 'PatchGroup', value: '' },
    ],
  },

  // AUTOMATION & LIFECYCLE TEMPLATES
  'automation-lifecycle': {
    name: 'Automation Lifecycle Template',
    description: 'Tags for automated resource lifecycle management',
    category: 'operations',
    icon: <EngineeringIcon />,
    color: '#1976d2',
    tags: [
      { key: 'AutoShutdown', value: 'enabled' },
      { key: 'LifecycleStage', value: 'active' },
      { key: 'AutomationLevel', value: 'full' },
      { key: 'MaintenanceWindow', value: 'weekend' },
    ],
  },
  'backup-disaster-recovery': {
    name: 'Backup & DR Template',
    description: 'Backup and disaster recovery configuration',
    category: 'operations',
    icon: <EngineeringIcon />,
    color: '#1976d2',
    tags: [
      { key: 'BackupSchedule', value: 'daily' },
      { key: 'BackupRetention', value: '30days' },
      { key: 'DisasterRecovery', value: 'enabled' },
      { key: 'RTO', value: '4hours' },
      { key: 'RPO', value: '1hour' },
    ],
  },
  'monitoring-alerting': {
    name: 'Monitoring & Alerting Template',
    description: 'Monitoring and alerting configuration',
    category: 'operations',
    icon: <EngineeringIcon />,
    color: '#1976d2',
    tags: [
      { key: 'MonitoringLevel', value: 'standard' },
      { key: 'AlertingEnabled', value: 'yes' },
      { key: 'OnCallTeam', value: '' },
      { key: 'EscalationLevel', value: 'standard' },
    ],
  },

  // DEVELOPMENT & DEPLOYMENT TEMPLATES
  'environment': {
    name: 'Environment Template',
    description: 'Environment and deployment tags',
    category: 'development',
    icon: <CodeIcon />,
    color: '#ed6c02',
    tags: [
      { key: 'Environment', value: '' },
      { key: 'DeploymentStage', value: '' },
      { key: 'Version', value: '' },
      { key: 'ManagedBy', value: 'TagOps' },
    ],
  },
  'ci-cd-pipeline': {
    name: 'CI/CD Pipeline Template',
    description: 'Continuous integration and deployment tags',
    category: 'development',
    icon: <CodeIcon />,
    color: '#ed6c02',
    tags: [
      { key: 'PipelineStage', value: '' },
      { key: 'BuildNumber', value: '' },
      { key: 'GitBranch', value: '' },
      { key: 'DeploymentMethod', value: 'automated' },
      { key: 'RollbackEnabled', value: 'yes' },
    ],
  },
  'application-architecture': {
    name: 'Application Architecture Template',
    description: 'Application and architecture classification',
    category: 'development',
    icon: <CodeIcon />,
    color: '#ed6c02',
    tags: [
      { key: 'Application', value: '' },
      { key: 'ApplicationTier', value: '' },
      { key: 'ArchitecturePattern', value: '' },
      { key: 'TechnologyStack', value: '' },
      { key: 'Dependencies', value: '' },
    ],
  },

  // DATA GOVERNANCE TEMPLATES
  'data-classification': {
    name: 'Data Classification Template',
    description: 'Data classification and governance',
    category: 'data-governance',
    icon: <DataObjectIcon />,
    color: '#7b1fa2',
    tags: [
      { key: 'DataClassification', value: 'internal' },
      { key: 'DataType', value: '' },
      { key: 'DataOwner', value: '' },
      { key: 'DataRetention', value: '7years' },
      { key: 'DataLineage', value: 'tracked' },
    ],
  },
  'data-privacy-gdpr': {
    name: 'Data Privacy GDPR Template',
    description: 'GDPR and data privacy compliance',
    category: 'data-governance',
    icon: <DataObjectIcon />,
    color: '#7b1fa2',
    tags: [
      { key: 'GDPRCompliant', value: 'yes' },
      { key: 'DataSubjectRights', value: 'enabled' },
      { key: 'DataProcessingBasis', value: '' },
      { key: 'DataController', value: '' },
      { key: 'DataProcessor', value: '' },
    ],
  },

  // OPERATIONS TEMPLATES
  'operations': {
    name: 'Operations Template',
    description: 'Operational management tags',
    category: 'operations',
    icon: <EngineeringIcon />,
    color: '#1976d2',
    tags: [
      { key: 'Team', value: '' },
      { key: 'OnCall', value: '' },
      { key: 'MaintenanceWindow', value: '' },
      { key: 'BackupSchedule', value: 'Daily' },
    ],
  },
  'incident-management': {
    name: 'Incident Management Template',
    description: 'Incident management and response tags',
    category: 'operations',
    icon: <EngineeringIcon />,
    color: '#1976d2',
    tags: [
      { key: 'IncidentPriority', value: 'medium' },
      { key: 'ResponseTeam', value: '' },
      { key: 'EscalationPath', value: 'standard' },
      { key: 'CommunicationPlan', value: 'enabled' },
    ],
  },
  'capacity-planning': {
    name: 'Capacity Planning Template',
    description: 'Capacity planning and resource sizing',
    category: 'operations',
    icon: <EngineeringIcon />,
    color: '#1976d2',
    tags: [
      { key: 'CapacityTier', value: 'standard' },
      { key: 'ScalingPolicy', value: 'auto' },
      { key: 'ResourceQuota', value: '' },
      { key: 'PerformanceTier', value: 'standard' },
    ],
  },
};

// Helper to get rule names that contain a given tag key-value pair (exact match)
const getRulesContainingTag = (
  rules: Record<string, TagRule>,
  templates: Record<string, TagTemplate>,
  tagKey: string,
  tagValue: string,
  excludeRuleName?: string | null
): string[] => {
  if (!tagKey?.trim()) return [];
  const result: string[] = [];
  const normalizedKey = tagKey.trim();
  const normalizedValue = (tagValue ?? '').trim();

  Object.entries(rules).forEach(([ruleName, rule]) => {
    if (excludeRuleName && ruleName === excludeRuleName) return;

    if (rule.rule?.type === 'add' || rule.rule?.type === 'remove') {
      const ruleTags = (rule.rule.tags || []) as Array<Record<string, string>>;
      const hasMatch = ruleTags.some(tagObj => {
        const k = Object.keys(tagObj)[0]?.trim() ?? '';
        const v = (Object.values(tagObj)[0] ?? '').trim();
        return k === normalizedKey && v === normalizedValue;
      });
      if (hasMatch) result.push(ruleName);
    } else if (rule.rule?.type === 'template' && rule.rule.templateName) {
      const template = templates[rule.rule.templateName];
      const constantTags = template?.constantTags ?? [];
      const hasMatch = constantTags.some(tagObj => {
        const k = Object.keys(tagObj)[0]?.trim() ?? '';
        const v = (Object.values(tagObj)[0] ?? '').trim();
        return k === normalizedKey && v === normalizedValue;
      });
      if (hasMatch) result.push(ruleName);
    }
  });
  return result;
};

// Helper to get rules with similar tag key (same key case-insensitive, different casing) - regardless of value
const getRulesContainingSimilarTag = (
  rules: Record<string, TagRule>,
  templates: Record<string, TagTemplate>,
  tagKey: string,
  _tagValue: string,
  excludeRuleName?: string | null
): Array<{ ruleName: string; existingKey: string }> => {
  if (!tagKey?.trim()) return [];
  const result: Array<{ ruleName: string; existingKey: string }> = [];
  const normalizedKey = tagKey.trim();
  const normalizedKeyLower = normalizedKey.toLowerCase();

  const collectSimilar = (ruleName: string, k: string) => {
    // Same key case-insensitive, but different casing (e.g. CostCenter vs Costcenter)
    if (k.toLowerCase() === normalizedKeyLower && k !== normalizedKey) {
      result.push({ ruleName, existingKey: k });
    }
  };

  Object.entries(rules).forEach(([ruleName, rule]) => {
    if (excludeRuleName && ruleName === excludeRuleName) return;

    if (rule.rule?.type === 'add' || rule.rule?.type === 'remove') {
      const ruleTags = (rule.rule.tags || []) as Array<Record<string, string>>;
      ruleTags.forEach(tagObj => {
        const k = Object.keys(tagObj)[0]?.trim() ?? '';
        collectSimilar(ruleName, k);
      });
    } else if (rule.rule?.type === 'template' && rule.rule.templateName) {
      const template = templates[rule.rule.templateName];
      const constantTags = template?.constantTags ?? [];
      constantTags.forEach(tagObj => {
        const k = Object.keys(tagObj)[0]?.trim() ?? '';
        collectSimilar(ruleName, k);
      });
    }
  });
  return result;
};

interface RuleWizardProfessionalProps {
  open: boolean;
  onClose: () => void;
  onSave: (rule: TagRule) => void;
  existingRule?: TagRule;
  templates?: Record<string, TagTemplate>;
  serviceConfig?: Record<string, ServiceConfig[]>;
  onboardedRegions?: string[];
  onboardedAccounts?: Array<{accountId: string, account_name: string}>;
  selectedTemplate?: any;
  /** All rules (for duplicate tag check across rules) */
  rules?: Record<string, TagRule>;
  /** Name of rule being edited (to exclude from duplicate check) */
  editingRuleName?: string | null;
  /** Callback to switch to editing another rule (closes current, opens for that rule) */
  onEditOtherRule?: (ruleName: string) => void;
}

export const RuleWizardProfessional: React.FC<RuleWizardProfessionalProps> = ({
  open,
  onClose,
  onSave,
  existingRule,
  templates = {},
  serviceConfig = {},
  onboardedRegions = [],
  onboardedAccounts = [],
  selectedTemplate,
  rules = {},
  editingRuleName = null,
  onEditOtherRule,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [ruleData, setRuleData] = useState<TagRule>(
    existingRule || {
      name: '',
      description: '',
      category: '',
      enabled: true,
      priority: 1,
      rule: {
        type: 'add',
        tags: [],
      },
      condition: [],
      conditionOperation: 'single',
    }
  );

  const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({});
  const [tagValidationErrors, setTagValidationErrors] = useState<Record<string, string[]>>({});
  const [duplicateTagWarnings, setDuplicateTagWarnings] = useState<Record<number, string[]>>({});
  const [similarTagWarnings, setSimilarTagWarnings] = useState<Record<number, Array<{ ruleName: string; existingKey: string }>>>({});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- setter kept for potential future use
  const [blacklistedTagError, setBlacklistedTagError] = useState<string>('');
  const [isValid, setIsValid] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadedServiceConfig, setLoadedServiceConfig] = useState<Record<string, any[]>>({});
  const [templateSearchQuery, setTemplateSearchQuery] = useState<string>('');
  const [organizations, setOrganizations] = useState<Array<{
    organizationManagementAccountId: string;
    organization_name: string;
  }>>([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [createdTimePickerIndex, setCreatedTimePickerIndex] = useState<number | null>(null);
  const [createdTimePickerValue, setCreatedTimePickerValue] = useState<Date>(new Date());
  const [createdTimePickerMonth, setCreatedTimePickerMonth] = useState<Date>(startOfMonth(new Date()));

  // Load service configuration
  useEffect(() => {
    const loadServiceConfig = async () => {
      try {
        const config = await tagopsService.getServiceConfig();
        setLoadedServiceConfig(config.services);
      } catch (error) {
        console.error('Failed to load service configuration:', error);
        // Fallback to passed serviceConfig prop
        setLoadedServiceConfig(serviceConfig);
      }
    };

    if (open) {
      loadServiceConfig();
    }
  }, [open, serviceConfig]);

  // Load organizations
  useEffect(() => {
    const loadOrganizations = async () => {
      if (!open) return;
      
      setLoadingOrganizations(true);
      try {
        const response = await tagopsService.getOrganizations();
        const orgs = (response.organizations || []).map((org: any) => ({
          organizationManagementAccountId: org.organizationManagementAccountId || org.type?.split('#')[1] || '',
          organization_name: org.organization_name || 'Unnamed Organization'
        })).filter((org: any) => org.organizationManagementAccountId); // Filter out invalid entries
        setOrganizations(orgs);
      } catch (error) {
        console.error('Failed to load organizations:', error);
        setOrganizations([]);
      } finally {
        setLoadingOrganizations(false);
      }
    };

    loadOrganizations();
  }, [open]);

  /** Another rule (not the one being edited) already uses allow_unknown_creation_time */
  const ruleNameUsingAllowUnknownCreationTime = useMemo(() => {
    for (const [name, r] of Object.entries(rules)) {
      if (editingRuleName != null && name === editingRuleName) continue;
      const conds = r.condition || [];
      for (const c of conds) {
        if (
          c.type === 'createdTime' &&
          Array.isArray(c.flags) &&
          c.flags.includes(ALLOW_UNKNOWN_CREATION_TIME_FLAG)
        ) {
          return name;
        }
      }
    }
    return null as string | null;
  }, [rules, editingRuleName]);

  // State for dynamic resource type options
  const [resourceTypeOptions, setResourceTypeOptions] = useState<string[]>([]);
  const [serviceTypeOptions, setServiceTypeOptions] = useState<string[]>([]);

  // Load dynamic resource type options from API
  useEffect(() => {
    const loadResourceTypeOptions = async () => {
      try {
        // Get supported services and resource types from API
        const supportedServices = await tagopsService.getSupportedServices();
        const options: string[] = [];
        
        // Extract resource types from API response
        Object.entries(supportedServices).forEach(([service, resourceTypes]) => {
          if (Array.isArray(resourceTypes)) {
            resourceTypes.forEach(resourceType => {
              options.push(`${service}:${resourceType}`);
            });
          }
        });
        
        // If no resource types found from API, fall back to service config
        if (options.length === 0) {
          console.warn('No resource types found from API, falling back to service configuration');
          const configToUse = Object.keys(loadedServiceConfig).length > 0 ? loadedServiceConfig : serviceConfig;
          const fallbackOptions: string[] = [];
          Object.entries(configToUse).forEach(([service, configs]) => {
            configs.forEach((config) => {
              fallbackOptions.push(`${service}:${config.type}`);
            });
          });
          setResourceTypeOptions(fallbackOptions.sort());
        } else {
          setResourceTypeOptions(options.sort());
        }
      } catch (error) {
        console.error('Error fetching supported services:', error);
        // Fall back to service config if API fails
        const options: string[] = [];
        const configToUse = Object.keys(loadedServiceConfig).length > 0 ? loadedServiceConfig : serviceConfig;
        Object.entries(configToUse).forEach(([service, configs]) => {
          configs.forEach((config) => {
            options.push(`${service}:${config.type}`);
          });
        });
        setResourceTypeOptions(options.sort());
      }
    };

    const loadServiceTypeOptions = async () => {
      try {
        // Get supported services from API
        const supportedServices = await tagopsService.getSupportedServices();
        const services = Object.keys(supportedServices);
        
        // If no services found from API, fall back to service config
        if (services.length === 0) {
          console.warn('No services found from API, falling back to service configuration');
          const configToUse = Object.keys(loadedServiceConfig).length > 0 ? loadedServiceConfig : serviceConfig;
          setServiceTypeOptions(Object.keys(configToUse).sort());
        } else {
          setServiceTypeOptions(services.sort());
        }
      } catch (error) {
        console.error('Error fetching supported services:', error);
        // Fall back to service config if API fails
        const configToUse = Object.keys(loadedServiceConfig).length > 0 ? loadedServiceConfig : serviceConfig;
        setServiceTypeOptions(Object.keys(configToUse).sort());
      }
    };

    if (open) {
      loadResourceTypeOptions();
      loadServiceTypeOptions();
    }
  }, [open, loadedServiceConfig, serviceConfig]);

  // Generate resource type options (fallback to state)
  const getResourceTypeOptions = useMemo(() => {
    return resourceTypeOptions;
  }, [resourceTypeOptions]);

  // Generate service type options (fallback to state)
  const getServiceTypeOptions = useMemo(() => {
    return serviceTypeOptions;
  }, [serviceTypeOptions]);

  // Function to validate individual tag
  const validateIndividualTag = (tagKey: string, tagValue: string, tagIndex: number): boolean => {
    const validation = validateAWSTag(tagKey, tagValue);
    if (!validation.isValid) {
      setTagValidationErrors(prev => ({
        ...prev,
        [`tag_${tagIndex}`]: validation.errors
      }));
      return false;
    } else {
      setTagValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`tag_${tagIndex}`];
        return newErrors;
      });
      return true;
    }
  };

  // Check if tag key-value exists in other rules (exact and similar) and update warnings
  const checkDuplicateTag = (tagKey: string, tagValue: string, tagIndex: number) => {
    if (!tagKey?.trim() || Object.keys(rules).length === 0) {
      setDuplicateTagWarnings(prev => {
        const next = { ...prev };
        delete next[tagIndex];
        return next;
      });
      setSimilarTagWarnings(prev => {
        const next = { ...prev };
        delete next[tagIndex];
        return next;
      });
      return;
    }
    const duplicateRules = getRulesContainingTag(
      rules,
      templates,
      tagKey,
      tagValue,
      editingRuleName
    );
    const similarRules = getRulesContainingSimilarTag(
      rules,
      templates,
      tagKey,
      tagValue,
      editingRuleName
    );
    setDuplicateTagWarnings(prev => {
      const next = { ...prev };
      if (duplicateRules.length > 0) {
        next[tagIndex] = duplicateRules;
      } else {
        delete next[tagIndex];
      }
      return next;
    });
    setSimilarTagWarnings(prev => {
      const next = { ...prev };
      if (similarRules.length > 0) {
        next[tagIndex] = similarRules;
      } else {
        delete next[tagIndex];
      }
      return next;
    });
  };

  // Function to get condition validation error message
  const getConditionErrorMessage = (condition: RuleCondition): string | null => {
    // Special validation for tagKeyValue condition type
    if (condition.type === 'tagKeyValue') {
      // Check tag_key_value
      if (condition.tag_key_operator === 'isIn' || condition.tag_key_operator === 'isNotIn') {
        if (!Array.isArray(condition.tag_key_value) || condition.tag_key_value.length === 0) {
          return 'At least one tag key value is required';
        }
      } else {
        if (!condition.tag_key_value || (typeof condition.tag_key_value === 'string' && !condition.tag_key_value.trim())) {
          return 'Tag Key Value is required';
        }
      }
      
      // Check tag_value_value
      if (condition.tag_value_operator === 'isIn' || condition.tag_value_operator === 'isNotIn') {
        if (!Array.isArray(condition.tag_value_value) || condition.tag_value_value.length === 0) {
          return 'At least one tag value value is required';
        }
      } else {
        if (!condition.tag_value_value || (typeof condition.tag_value_value === 'string' && !condition.tag_value_value.trim())) {
          return 'Tag Value Value is required';
        }
      }
      return null;
    }

    if (condition.type === 'createdTime') {
      const epoch = parseCreationEpochValue(condition.value);
      if (Number.isNaN(epoch) || epoch <= 0) {
        return 'A valid cutoff date and time is required';
      }
      return null;
    }
    
    if (Array.isArray(condition.value)) {
      return condition.value.length === 0 ? 'At least one value is required' : null;
    }
    
    const stringValue = condition.value != null ? condition.value.toString().trim() : '';
    if (!stringValue) return 'Value is required';
    
    return null;
  };

  // Function to check if a condition has validation errors
  const hasConditionError = (condition: RuleCondition): boolean => {
    return getConditionErrorMessage(condition) !== null;
  };

  const getStepStatus = (index: number): 'completed' | 'current' | 'error' | 'upcoming' => {
    const hasErrors = (window as any).stepErrors?.[index]?.length > 0;
    const isCompleted = activeStep > index;
    const isCurrent = activeStep === index;
    
    if (hasErrors) return 'error';
    if (isCompleted) return 'completed';
    if (isCurrent) return 'current';
    return 'upcoming';
  };

  const steps: StepType[] = [
    {
      id: 'basic',
      label: 'Basic Information',
      description: 'Define rule name, description, and category',
      status: getStepStatus(0),
      icon: <Info className="w-6 h-6" />,
    },
    {
      id: 'conditions',
      label: 'Conditions',
      description: 'Set conditions for when the rule applies',
      status: getStepStatus(1),
      icon: <ListChecks className="w-6 h-6" />,
    },
    {
      id: 'actions',
      label: 'Actions',
      description: 'Define what tags to apply or remove',
      status: getStepStatus(2),
      icon: <Zap className="w-6 h-6" />,
    },
    {
      id: 'advanced',
      label: 'Advanced Settings',
      description: 'Configure priority, scheduling, and validation',
      status: getStepStatus(3),
      icon: <Settings className="w-6 h-6" />,
    },
    {
      id: 'review',
      label: 'Review & Save',
      description: 'Review your rule configuration',
      status: getStepStatus(4),
      icon: <CheckCircle className="w-6 h-6" />,
    },
  ];

  const validateRule = useCallback(() => {
    const errors: Record<string, string[]> = {};
    const stepErrors: Record<number, string[]> = {};

    // Validate basic information (Step 0)
    if (!ruleData.name?.trim()) {
      errors.name = ['Rule name is required'];
      stepErrors[0] = stepErrors[0] || [];
      stepErrors[0].push('Rule name is required');
    }

    if (!ruleData.category) {
      errors.category = ['Category is required'];
      stepErrors[0] = stepErrors[0] || [];
      stepErrors[0].push('Category is required');
    }

    // Validate conditions (Step 1)
    if (!ruleData.condition || ruleData.condition.length === 0) {
      errors.conditions = ['At least one condition is required'];
      stepErrors[1] = stepErrors[1] || [];
      stepErrors[1].push('At least one condition is required');
    } else {
      // Check if any condition has empty values
      const hasEmptyConditions = ruleData.condition.some(condition => {
        // Special handling for tagKeyValue condition type
        if (condition.type === 'tagKeyValue') {
          // Check tag_key_value
          if (condition.tag_key_operator === 'isIn' || condition.tag_key_operator === 'isNotIn') {
            if (!Array.isArray(condition.tag_key_value) || condition.tag_key_value.length === 0) {
              return true;
            }
          } else {
            if (!condition.tag_key_value || (typeof condition.tag_key_value === 'string' && !condition.tag_key_value.trim())) {
              return true;
            }
          }

          // Check tag_value_value
          if (condition.tag_value_operator === 'isIn' || condition.tag_value_operator === 'isNotIn') {
            if (!Array.isArray(condition.tag_value_value) || condition.tag_value_value.length === 0) {
              return true;
            }
          } else {
            if (!condition.tag_value_value || (typeof condition.tag_value_value === 'string' && !condition.tag_value_value.trim())) {
              return true;
            }
          }
          return false;
        }

        if (condition.type === 'createdTime') {
          const epoch = parseCreationEpochValue(condition.value);
          return Number.isNaN(epoch) || epoch <= 0;
        }

        // For other condition types, check the value field
        if (Array.isArray(condition.value)) {
          return condition.value.length === 0;
        }
        if (typeof condition.value === 'number') {
          return Number.isNaN(condition.value);
        }
        return !condition.value || condition.value.toString().trim() === '';
      });

      if (hasEmptyConditions) {
        errors.conditions = ['Please fill in all condition values'];
        stepErrors[1] = stepErrors[1] || [];
        stepErrors[1].push('Please fill in all condition values');
      }
    }

    // Validate actions (Step 2)
    if (ruleData.rule?.type === 'template' && !ruleData.rule.templateName) {
      errors.template = ['Template is required'];
      stepErrors[2] = stepErrors[2] || [];
      stepErrors[2].push('Template is required');
    }

    if (ruleData.rule?.type === 'add' &&
        (!ruleData.rule.tags || ruleData.rule.tags.length === 0)) {
      errors.tags = ['At least one tag is required'];
      stepErrors[2] = stepErrors[2] || [];
      stepErrors[2].push('At least one tag is required');
    }

    // Validate individual tags for empty keys or values
    if (ruleData.rule?.type === 'add' && ruleData.rule.tags && ruleData.rule.tags.length > 0) {
      const tagErrors: string[] = [];
      ruleData.rule.tags.forEach((tag, index) => {
        const tagKey = Object.keys(tag)[0] || '';
        const tagValue = Object.values(tag)[0] || '';

        if (!tagKey.trim()) {
          tagErrors.push(`Tag ${index + 1}: Key cannot be empty`);
        }
        if (!tagValue.trim()) {
          tagErrors.push(`Tag ${index + 1}: Value cannot be empty`);
        }
      });

      if (tagErrors.length > 0) {
        errors.tags = tagErrors;
        stepErrors[2] = stepErrors[2] || [];
        stepErrors[2].push(...tagErrors);
      }
    }

    setValidationErrors(errors);
    // Store step errors for navigation
    (window as any).stepErrors = stepErrors;
    setIsValid(Object.keys(errors).length === 0);
  }, [ruleData]);

  useEffect(() => {
    validateRule();
  }, [ruleData, validateRule]);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setDuplicateTagWarnings({});
      setSimilarTagWarnings({});
      if (existingRule) {
        // If editing an existing rule, use its data
        setRuleData(existingRule);
        setActiveStep(0);
      } else {
        // If creating a new rule, reset to default state with an empty condition
        setRuleData({
          name: '',
          description: '',
          category: '',
          enabled: true,
          priority: 1,
          rule: {
            type: 'add',
            tags: [],
          },
          condition: [{
            type: 'resourceType',
            operator: 'equal',
            value: [],
          }],
          conditionOperation: 'single',
        });
        setActiveStep(0);
      }
    }
  }, [open, existingRule]);

  // Handle selected template
  useEffect(() => {
    if (selectedTemplate && open) {
      // Apply template data to the rule
      const templateTags = selectedTemplate.tags.map((tag: any) => ({ [tag.key]: tag.value }));
      setRuleData(prev => ({
        ...prev,
        category: selectedTemplate.category || prev.category,
        description: selectedTemplate.description || prev.description,
        rule: {
          type: 'add',
          tags: templateTags,
        },
      }));
      // Start at step 0 (rule name) for better user experience
      setActiveStep(0);
    }
  }, [selectedTemplate, open]);

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleSave = async () => {
    if (!isValid) {
      toast.error('Please fix validation errors before saving');
      return;
    }

    // Validate all tags before saving
    const currentTags = (ruleData.rule?.tags as Array<Record<string, string>>) || [];
    let hasTagErrors = false;
    
    currentTags.forEach((tag, index) => {
      const tagKey = Object.keys(tag)[0] || '';
      const tagValue = Object.values(tag)[0] || '';
      const isValid = validateIndividualTag(tagKey, tagValue, index);
      if (!isValid) {
        hasTagErrors = true;
      }
    });

    if (hasTagErrors) {
      toast.error('Please fix tag validation errors before saving');
      return;
    }

    setSaving(true);
    try {
      await onSave(ruleData);
      toast.success('Rule saved successfully');
      onClose();
    } catch (error) {
      toast.error('Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const updateRuleData = (updates: Partial<TagRule>) => {
    setRuleData(prev => ({ ...prev, ...updates }));
  };

  const openCreatedTimePicker = useCallback((conditionIndex: number, currentValue: RuleCondition['value']) => {
    const epoch = parseCreationEpochValue(currentValue);
    const initialValue =
      !Number.isNaN(epoch) && epoch > 0 ? new Date(epoch * 1000) : new Date();
    setCreatedTimePickerValue(initialValue);
    setCreatedTimePickerMonth(startOfMonth(initialValue));
    setCreatedTimePickerIndex(conditionIndex);
  }, []);

  const closeCreatedTimePicker = useCallback(() => {
    setCreatedTimePickerIndex(null);
  }, []);

  const applyCreatedTimePicker = useCallback(() => {
    if (createdTimePickerIndex == null) return;
    const nextEpoch = Math.floor(createdTimePickerValue.getTime() / 1000);
    setRuleData(prev => ({
      ...prev,
      condition:
        prev.condition?.map((c, i) =>
          i === createdTimePickerIndex
            ? {
                ...c,
                value: nextEpoch,
              }
            : c
        ) || [],
    }));
    setCreatedTimePickerIndex(null);
  }, [createdTimePickerIndex, createdTimePickerValue]);

  const renderBasicInformationStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Basic Information
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Define the basic properties of your tagging rule.
      </Typography>

      {/* Validation Summary for this step */}
      {(validationErrors.name || validationErrors.category) && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Required Fields Missing</AlertTitle>
          <Box sx={{ ml: 2 }}>
                             {validationErrors.name && (
                   <Typography variant="body2" color="error" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                     <ErrorIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                     <strong>Rule Name:</strong> {validationErrors.name.join(', ')}
                   </Typography>
                 )}
                 {validationErrors.category && (
                   <Typography variant="body2" color="error" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                     <ErrorIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                     <strong>Category:</strong> {validationErrors.category.join(', ')}
                   </Typography>
                 )}
          </Box>
        </Alert>
      )}

      <Grid container spacing={3}>
                 <Grid item xs={12}>
           <TextField
             fullWidth
             label="Rule Name"
             value={ruleData.name || ''}
             onChange={(e) => updateRuleData({ name: e.target.value })}
             placeholder="e.g., Production Cost Center Rule"
             error={!!validationErrors.name}
             helperText={validationErrors.name?.join(', ') || `${(ruleData.name || '').length}/64 characters`}
             required
             inputProps={{ maxLength: 64 }}
           />
         </Grid>

                 <Grid item xs={12}>
           <TextField
             fullWidth
             label="Description"
             value={ruleData.description || ''}
             onChange={(e) => updateRuleData({ description: e.target.value })}
             placeholder="Describe what this rule does and when it should be applied"
             multiline
             rows={3}
             inputProps={{ maxLength: 512 }}
             helperText={`${(ruleData.description || '').length}/512 characters`}
           />
         </Grid>

                                   <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Category
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Select a category to organize your rule.
            </Typography>
                       <Grid container spacing={2}>
              {Object.entries(RULE_CATEGORIES).map(([key, category]) => (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: ruleData.category === key ? category.color : 'divider',
                      bgcolor: ruleData.category === key ? `${category.color}10` : 'background.paper',
                      '&:hover': {
                        borderColor: category.color,
                        bgcolor: `${category.color}05`,
                      },
                    }}
                    onClick={() => {
                      updateRuleData({ category: key });
                    }}
                  >
                                       <CardContent>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Box color={category.color}>
                          {category.icon}
                        </Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {category.name}
                        </Typography>
                        {ruleData.category === key && (
                          <CheckCircleIcon 
                            sx={{ 
                              color: category.color, 
                              ml: 'auto',
                              fontSize: 20 
                            }} 
                          />
                        )}
                      </Box>
                     <Typography variant="body2" color="text.secondary">
                       {category.description}
                     </Typography>
                   </CardContent>
                 </Card>
               </Grid>
             ))}
           </Grid>
         </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Built-in Templates
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Start with a professional template or create from scratch.
              </Typography>
            </Box>
            <TextField
              size="small"
              placeholder="Search templates..."
              value={templateSearchQuery}
              onChange={(e) => setTemplateSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 200 }}
            />
          </Box>
          
          <Box 
            sx={{ 
              maxHeight: 400, 
              overflowY: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 1,
              bgcolor: 'background.paper'
            }}
          >
            <Grid container spacing={1}>
              {Object.entries(PROFESSIONAL_TEMPLATES)
                .filter(([key, template]) => {
                  if (!templateSearchQuery) return true;
                  const query = templateSearchQuery.toLowerCase();
                  return (
                    template.name.toLowerCase().includes(query) ||
                    template.description.toLowerCase().includes(query) ||
                    template.category.toLowerCase().includes(query) ||
                    template.tags.some(tag => 
                      tag.key.toLowerCase().includes(query) || 
                      tag.value.toLowerCase().includes(query)
                    )
                  );
                })
                .map(([key, template]) => {
               // Check if this template is currently applied
               const isApplied = ruleData.rule?.type === 'add' && 
                 ruleData.rule.tags && 
                 ruleData.rule.tags.length === template.tags.length &&
                 template.tags.every(templateTag => 
                   ruleData.rule.tags?.some(ruleTag => 
                     Object.keys(ruleTag)[0] === templateTag.key && 
                     Object.values(ruleTag)[0] === templateTag.value
                   ) || false
                 );
               
               return (
                 <Grid item xs={12} sm={6} md={4} key={key}>
                   <Paper
                     sx={{
                       p: 1.5,
                       cursor: 'pointer',
                       border: '1px solid',
                       borderColor: isApplied ? template.color : 'divider',
                       bgcolor: isApplied ? `${template.color}10` : 'background.paper',
                       transition: 'all 0.2s ease-in-out',
                       '&:hover': {
                         borderColor: template.color,
                         bgcolor: `${template.color}05`,
                         transform: 'translateY(-1px)',
                         boxShadow: 2,
                       },
                     }}
                     onClick={() => {
                       // Apply template
                       updateRuleData({
                         rule: {
                           type: 'add',
                           tags: template.tags.map(tag => ({ [tag.key]: tag.value })),
                         },
                       });
                       toast.success(`Applied ${template.name}`);
                     }}
                   >
                     <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                       <Box color={template.color} sx={{ display: 'flex', alignItems: 'center' }}>
                         {template.icon}
                       </Box>
                       <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: '0.9rem' }}>
                         {template.name}
                       </Typography>
                       {isApplied && (
                         <CheckCircleIcon 
                           sx={{ 
                             color: template.color, 
                             ml: 'auto',
                             fontSize: 16 
                           }} 
                         />
                       )}
                     </Box>
                     <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                       {template.description}
                     </Typography>
                     <Box display="flex" gap={0.25} flexWrap="wrap" mt={0.5}>
                       {template.tags.slice(0, 3).map((tag, index) => (
                         <Chip
                           key={index}
                           label={`${tag.key}${tag.value ? '=' + tag.value : ''}`}
                           size="small"
                           variant="outlined"
                           sx={{ 
                             fontSize: '0.65rem', 
                             height: 20,
                             '& .MuiChip-label': { px: 0.5 }
                           }}
                         />
                       ))}
                       {template.tags.length > 3 && (
                         <Chip
                           label={`+${template.tags.length - 3} more`}
                           size="small"
                           variant="outlined"
                           sx={{ 
                             fontSize: '0.65rem', 
                             height: 20,
                             '& .MuiChip-label': { px: 0.5 }
                           }}
                         />
                       )}
                     </Box>
                   </Paper>
                 </Grid>
               );
             })}
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );

  const renderConditionsStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Conditions
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Define when this rule should be applied to resources.
      </Typography>

      {/* Validation Summary for this step */}
      {validationErrors.conditions && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Condition Issues</AlertTitle>
          <Box sx={{ ml: 2 }}>
                             <Typography variant="body2" color="error" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                   <ErrorIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                   <strong>Conditions:</strong> {validationErrors.conditions.join(', ')}
                 </Typography>
          </Box>
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Condition Operation</InputLabel>
                         <Select
               value={ruleData.conditionOperation || 'single'}
               label="Condition Operation"
               onChange={(e) => {
                 const newOperation = e.target.value as any;
                 let newConditions = ruleData.condition || [];
                 
                 // If switching to single condition, keep only the first condition
                 if (newOperation === 'single' && newConditions.length > 1) {
                   newConditions = [newConditions[0]];
                 }
                 
                 updateRuleData({ 
                   conditionOperation: newOperation,
                   condition: newConditions
                 });
               }}
             >
              <MenuItem value="single">Single Condition (One condition only)</MenuItem>
              <MenuItem value="and">AND (All conditions must match)</MenuItem>
              <MenuItem value="or">OR (Any condition can match)</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            Resource Conditions
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Define conditions based on resource properties, tags, or other attributes.
          </Typography>

                     {ruleData.condition?.map((condition, index) => (
             <Paper 
               key={index} 
               sx={{ 
                 p: 2, 
                 mb: 2, 
                 border: '1px solid', 
                 borderColor: hasConditionError(condition) ? 'error.main' : 'divider',
                 bgcolor: 'background.paper'
               }}
             >
               <Box display="flex" alignItems="center" gap={2} mb={2}>
                 <Typography variant="subtitle2">Condition {index + 1}</Typography>
                 {ruleData.conditionOperation !== 'single' && index > 0 && (
                   <Chip
                     label={ruleData.conditionOperation === 'and' ? 'AND' : 'OR'}
                     size="small"
                     color="primary"
                     variant="outlined"
                   />
                 )}
                 {hasConditionError(condition) && (
                   <Chip
                     label="Missing Value"
                     size="small"
                     color="error"
                     variant="outlined"
                     icon={<ErrorIcon />}
                   />
                 )}
                 <IconButton
                   size="small"
                   onClick={() => {
                     const newConditions = ruleData.condition?.filter((_, i) => i !== index) || [];
                     updateRuleData({ condition: newConditions });
                   }}
                   color="error"
                 >
                   <DeleteIcon />
                 </IconButton>
               </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={condition.type}
                      label="Type"
                                             onChange={(e) => {
                        const newType = e.target.value as any;
                        let updatedCondition: any = { ...condition, type: newType };
                        
                        if (newType === 'tagKeyValue') {
                          // Initialize tagKeyValue specific fields
                          const tagKeyOp = condition.tag_key_operator || 'equal';
                          const tagValueOp = condition.tag_value_operator || 'equal';
                          const isTagKeyList = tagKeyOp === 'isIn' || tagKeyOp === 'isNotIn';
                          const isTagValueList = tagValueOp === 'isIn' || tagValueOp === 'isNotIn';
                          
                          updatedCondition = {
                            ...updatedCondition,
                            tag_key_operator: tagKeyOp,
                            tag_key_value: isTagKeyList ? [] : '', // Reset value when changing type
                            tag_value_operator: tagValueOp,
                            tag_value_value: isTagValueList ? [] : '', // Reset value when changing type
                            operator: undefined,
                            value: undefined
                          };
                        } else {
                          // For other types, reset value based on current operator
                          let currentOperator = condition.operator || 'equal';
                          // Reset organization operators if type is not accountId
                          if (newType !== 'accountId' && (currentOperator === 'isInOrganization' || currentOperator === 'isNotInOrganization')) {
                            currentOperator = 'equal';
                          }
                          if (newType !== 'createdTime' && (currentOperator === 'createdBefore' || currentOperator === 'createdAfter')) {
                            currentOperator = 'equal';
                          }
                          if (newType === 'createdTime') {
                            currentOperator = 'createdBefore';
                          }
                          const isListOperator = currentOperator === 'isIn' || currentOperator === 'isNotIn';
                          const resetValue =
                            newType === 'createdTime'
                              ? Math.floor(Date.now() / 1000)
                              : isListOperator
                                ? []
                                : '';
                          
                          updatedCondition = {
                            ...updatedCondition,
                            operator: currentOperator,
                            value: resetValue, // Reset value when changing type
                            tag_key_operator: undefined,
                            tag_key_value: undefined,
                            tag_value_operator: undefined,
                            tag_value_value: undefined,
                            flags: undefined,
                          };
                        }
                        
                        const newConditions = ruleData.condition?.map((c, i) => 
                          i === index ? updatedCondition : c
                        ) || [];
                        updateRuleData({ condition: newConditions });
                      }}
                    >
                      <MenuItem value="resourceType">Resource Type</MenuItem>
                      <MenuItem value="serviceResourceType">Service</MenuItem>
                      <MenuItem value="region">Region</MenuItem>
                      <MenuItem value="accountId">Account ID</MenuItem>
                      <MenuItem value="name">Resource Name</MenuItem>
                      <MenuItem value="tagKey">Tag Key</MenuItem>
                      <MenuItem value="tagKeyValue">Tag Key Value</MenuItem>
                      <MenuItem value="createdTime">Created Time</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {condition.type === 'tagKeyValue' ? (
                  <>
                    {/* First row: Type, Tag Key Operator, Tag Key Value */}
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel>Tag Key Operator</InputLabel>
                        <Select
                          value={condition.tag_key_operator || 'equal'}
                          label="Tag Key Operator"
                          onChange={(e) => {
                            const newOperator = e.target.value as 'equal' | 'notEqual' | 'startsWith' | 'notStartsWith' | 'endsWith' | 'notEndsWith' | 'contain' | 'notContain' | 'contains' | 'notContains' | 'isIn' | 'isNotIn';
                            const isListOperator = newOperator === 'isIn' || newOperator === 'isNotIn';
                            const newValue = isListOperator ? [] : '';
                            const newConditions = ruleData.condition?.map((c, i) => 
                              i === index ? { ...c, tag_key_operator: newOperator, tag_key_value: newValue } : c
                            ) || [];
                            updateRuleData({ condition: newConditions });
                          }}
                        >
                          <MenuItem value="equal">Equal</MenuItem>
                          <MenuItem value="notEqual">Not Equal</MenuItem>
                          <MenuItem value="startsWith">Starts With</MenuItem>
                          <MenuItem value="notStartsWith">Not Starts With</MenuItem>
                          <MenuItem value="endsWith">Ends With</MenuItem>
                          <MenuItem value="notEndsWith">Not Ends With</MenuItem>
                          <MenuItem value="contains">Contains</MenuItem>
                          <MenuItem value="notContains">Not Contains</MenuItem>
                          <MenuItem value="isIn">Is In</MenuItem>
                          <MenuItem value="isNotIn">Is Not In</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      {(condition.tag_key_operator === 'isIn' || condition.tag_key_operator === 'isNotIn') ? (
                        <>
                          <Autocomplete
                            multiple
                            freeSolo
                            options={[]}
                            value={Array.isArray(condition.tag_key_value) ? condition.tag_key_value : []}
                            onChange={(event, newValue) => {
                              const newConditions = ruleData.condition?.map((c, i) => 
                                i === index ? { ...c, tag_key_value: newValue } : c
                              ) || [];
                              updateRuleData({ condition: newConditions });
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Tag Key Value"
                                placeholder="Enter tag keys"
                                helperText="Press Enter to add each tag key"
                                error={!Array.isArray(condition.tag_key_value) || condition.tag_key_value.length === 0}
                              />
                            )}
                            renderTags={() => null}
                            disableCloseOnSelect
                          />
                          {Array.isArray(condition.tag_key_value) && condition.tag_key_value.length > 0 && (
                            <Box mt={1}>
                              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                Selected Tag Keys:
                              </Typography>
                              <Box display="flex" gap={1} flexWrap="wrap">
                                {condition.tag_key_value.map((option, chipIndex) => (
                                  <Chip
                                    key={chipIndex}
                                    variant="outlined"
                                    label={option}
                                    size="small"
                                    onDelete={() => {
                                      const currentValue = Array.isArray(condition.tag_key_value) ? condition.tag_key_value : [];
                                      const newValue = currentValue.filter((_, i) => i !== chipIndex);
                                      const newConditions = ruleData.condition?.map((c, i) => 
                                        i === index ? { ...c, tag_key_value: newValue } : c
                                      ) || [];
                                      updateRuleData({ condition: newConditions });
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </>
                      ) : (
                        <TextField
                          fullWidth
                          label="Tag Key Value"
                          value={typeof condition.tag_key_value === 'string' ? condition.tag_key_value : ''}
                          onChange={(e) => {
                            const newConditions = ruleData.condition?.map((c, i) => 
                              i === index ? { ...c, tag_key_value: e.target.value } : c
                            ) || [];
                            updateRuleData({ condition: newConditions });
                          }}
                          placeholder="e.g., env"
                          helperText={!condition.tag_key_value ? "This field is required" : "Enter the tag key to match"}
                          error={!condition.tag_key_value || (typeof condition.tag_key_value === 'string' && !condition.tag_key_value.trim())}
                        />
                      )}
                    </Grid>
                    {/* Second row: Tag Value Operator and Tag Value Value - both on same line, same size (md={4}), not aligned with columns above */}
                    <Grid item xs={12} md={4} sx={{ ml: { md: '33.333%' } }}>
                      <FormControl fullWidth>
                        <InputLabel>Tag Value Operator</InputLabel>
                        <Select
                          value={condition.tag_value_operator || 'equal'}
                          label="Tag Value Operator"
                          onChange={(e) => {
                            const newOperator = e.target.value as 'equal' | 'notEqual' | 'startsWith' | 'notStartsWith' | 'endsWith' | 'notEndsWith' | 'contain' | 'notContain' | 'contains' | 'notContains' | 'isIn' | 'isNotIn';
                            const isListOperator = newOperator === 'isIn' || newOperator === 'isNotIn';
                            const newValue = isListOperator ? [] : '';
                            const newConditions = ruleData.condition?.map((c, i) => 
                              i === index ? { ...c, tag_value_operator: newOperator, tag_value_value: newValue } : c
                            ) || [];
                            updateRuleData({ condition: newConditions });
                          }}
                        >
                          <MenuItem value="equal">Equal</MenuItem>
                          <MenuItem value="notEqual">Not Equal</MenuItem>
                          <MenuItem value="startsWith">Starts With</MenuItem>
                          <MenuItem value="notStartsWith">Not Starts With</MenuItem>
                          <MenuItem value="endsWith">Ends With</MenuItem>
                          <MenuItem value="notEndsWith">Not Ends With</MenuItem>
                          <MenuItem value="contains">Contains</MenuItem>
                          <MenuItem value="notContains">Not Contains</MenuItem>
                          <MenuItem value="isIn">Is In</MenuItem>
                          <MenuItem value="isNotIn">Is Not In</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      {(condition.tag_value_operator === 'isIn' || condition.tag_value_operator === 'isNotIn') ? (
                        <>
                          <Autocomplete
                            multiple
                            freeSolo
                            options={[]}
                            value={Array.isArray(condition.tag_value_value) ? condition.tag_value_value : []}
                            onChange={(event, newValue) => {
                              const newConditions = ruleData.condition?.map((c, i) => 
                                i === index ? { ...c, tag_value_value: newValue } : c
                              ) || [];
                              updateRuleData({ condition: newConditions });
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Tag Value Value"
                                placeholder="Enter tag values"
                                helperText="Press Enter to add each tag value"
                                error={!Array.isArray(condition.tag_value_value) || condition.tag_value_value.length === 0}
                              />
                            )}
                            renderTags={() => null}
                            disableCloseOnSelect
                          />
                          {Array.isArray(condition.tag_value_value) && condition.tag_value_value.length > 0 && (
                            <Box mt={1}>
                              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                Selected Tag Values:
                              </Typography>
                              <Box display="flex" gap={1} flexWrap="wrap">
                                {condition.tag_value_value.map((option, chipIndex) => (
                                  <Chip
                                    key={chipIndex}
                                    variant="outlined"
                                    label={option}
                                    size="small"
                                    onDelete={() => {
                                      const currentValue = Array.isArray(condition.tag_value_value) ? condition.tag_value_value : [];
                                      const newValue = currentValue.filter((_, i) => i !== chipIndex);
                                      const newConditions = ruleData.condition?.map((c, i) => 
                                        i === index ? { ...c, tag_value_value: newValue } : c
                                      ) || [];
                                      updateRuleData({ condition: newConditions });
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </>
                      ) : (
                        <TextField
                          fullWidth
                          label="Tag Value Value"
                          value={typeof condition.tag_value_value === 'string' ? condition.tag_value_value : ''}
                          onChange={(e) => {
                            const newConditions = ruleData.condition?.map((c, i) => 
                              i === index ? { ...c, tag_value_value: e.target.value } : c
                            ) || [];
                            updateRuleData({ condition: newConditions });
                          }}
                          placeholder="e.g., dev"
                          helperText={!condition.tag_value_value ? "This field is required" : "Enter the tag value to match"}
                          error={!condition.tag_value_value || (typeof condition.tag_value_value === 'string' && !condition.tag_value_value.trim())}
                        />
                      )}
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Operator</InputLabel>
                      <Select
                        key={`operator-${condition.type}-${index}`}
                        value={condition.operator || 'equal'}
                        label="Operator"
                        onChange={(e) => {
                          const newOperator = e.target.value as 'equal' | 'notEqual' | 'startsWith' | 'notStartsWith' | 'endsWith' | 'notEndsWith' | 'contains' | 'notContains' | 'isIn' | 'isNotIn' | 'isInOrganization' | 'isNotInOrganization' | 'createdBefore' | 'createdAfter';
                          const isListOperator = newOperator === 'isIn' || newOperator === 'isNotIn';
                          const isOrganizationOperator = newOperator === 'isInOrganization' || newOperator === 'isNotInOrganization';
                          const isCreatedTimeOperator = newOperator === 'createdBefore' || newOperator === 'createdAfter';
                          let newValue: string | string[] | number = '';
                          if (isListOperator) newValue = [];
                          else if (isOrganizationOperator) newValue = '';
                          else if (isCreatedTimeOperator) {
                            const existing = parseCreationEpochValue(condition.value);
                            newValue = !Number.isNaN(existing) && existing > 0 ? existing : Math.floor(Date.now() / 1000);
                          }
                          
                          const newConditions = ruleData.condition?.map((c, i) => 
                            i === index ? { ...c, operator: newOperator, value: newValue } : c
                          ) || [];
                          updateRuleData({ condition: newConditions });
                        }}
                      >
                        <MenuItem value="equal">Equal</MenuItem>
                        <MenuItem value="notEqual">Not Equal</MenuItem>
                        <MenuItem value="startsWith">Starts With</MenuItem>
                        <MenuItem value="notStartsWith">Not Starts With</MenuItem>
                        <MenuItem value="endsWith">Ends With</MenuItem>
                        <MenuItem value="notEndsWith">Not Ends With</MenuItem>
                        <MenuItem value="contains">Contains</MenuItem>
                        <MenuItem value="notContains">Not Contains</MenuItem>
                        <MenuItem value="isIn">Is In</MenuItem>
                        <MenuItem value="isNotIn">Is Not In</MenuItem>
                        <MenuItem 
                          value="isInOrganization"
                          disabled={condition.type !== 'accountId'}
                        >
                          Is In Organization
                        </MenuItem>
                        <MenuItem 
                          value="isNotInOrganization"
                          disabled={condition.type !== 'accountId'}
                        >
                          Is Not In Organization
                        </MenuItem>
                        <MenuItem
                          value="createdBefore"
                          disabled={condition.type !== 'createdTime'}
                        >
                          Created Before
                        </MenuItem>
                        <MenuItem
                          value="createdAfter"
                          disabled={condition.type !== 'createdTime'}
                        >
                          Created After
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {condition.type !== 'tagKeyValue' && (
                  <Grid item xs={12} md={4}>
                    {condition.type === 'resourceType' && (
                    <>
                      {(condition.operator === 'isIn' || condition.operator === 'isNotIn') ? (
                        <>
                          <Autocomplete
                            multiple
                            options={getResourceTypeOptions}
                            value={Array.isArray(condition.value) ? condition.value : []}
                            onChange={(event, newValue) => {
                              const newConditions = ruleData.condition?.map((c, i) => 
                                i === index ? { ...c, value: newValue } : c
                              ) || [];
                              updateRuleData({ condition: newConditions });
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Resource Types"
                                placeholder="Select resource types"
                              />
                            )}
                            renderTags={() => null} // Don't render tags inline
                            disableCloseOnSelect
                          />
                          {Array.isArray(condition.value) && condition.value.length > 0 && (
                            <Box mt={1}>
                              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                Selected Resource Types:
                              </Typography>
                              <Box display="flex" gap={1} flexWrap="wrap">
                                {condition.value.map((option, chipIndex) => (
                                  <Chip
                                    key={chipIndex}
                                    variant="outlined"
                                    label={option}
                                    size="small"
                                    onDelete={() => {
                                      const currentValue = Array.isArray(condition.value) ? condition.value : [];
                                      const newValue = currentValue.filter((_, i) => i !== chipIndex);
                                      const newConditions = ruleData.condition?.map((c, i) => 
                                        i === index ? { ...c, value: newValue } : c
                                      ) || [];
                                      updateRuleData({ condition: newConditions });
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </>
                      ) : (condition.operator === 'equal' || condition.operator === 'notEqual') ? (
                        <Autocomplete
                          options={getResourceTypeOptions}
                          value={typeof condition.value === 'string' ? condition.value : ''}
                          onChange={(event, newValue) => {
                            const newConditions = ruleData.condition?.map((c, i) => 
                              i === index ? { ...c, value: newValue || '' } : c
                            ) || [];
                            updateRuleData({ condition: newConditions });
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Resource Type"
                              placeholder="e.g., ec2:instance"
                              helperText="Select the resource type to match"
                            />
                          )}
                        />
                      ) : (
                        <TextField
                          fullWidth
                          label="Resource Type"
                          value={typeof condition.value === 'string' ? condition.value : ''}
                          onChange={(e) => {
                            const newConditions = ruleData.condition?.map((c, i) => 
                              i === index ? { ...c, value: e.target.value } : c
                            ) || [];
                            updateRuleData({ condition: newConditions });
                          }}
                          placeholder="e.g., ec2:instance"
                          helperText={hasConditionError(condition) ? "This field is required" : "Enter the resource type to match"}
                          error={hasConditionError(condition)}
                        />
                      )}
                    </>
                  )}

                  {condition.type === 'region' && (
                    <>
                      {(condition.operator === 'isIn' || condition.operator === 'isNotIn') ? (
                        <>
                          <Autocomplete
                            multiple
                            options={onboardedRegions}
                            value={Array.isArray(condition.value) ? condition.value : []}
                            onChange={(event, newValue) => {
                              const newConditions = ruleData.condition?.map((c, i) => 
                                i === index ? { ...c, value: newValue } : c
                              ) || [];
                              updateRuleData({ condition: newConditions });
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Regions"
                                placeholder="Select regions"
                              />
                            )}
                            renderTags={() => null} // Don't render tags inline
                            disableCloseOnSelect
                          />
                          {Array.isArray(condition.value) && condition.value.length > 0 && (
                            <Box mt={1}>
                              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                Selected Regions:
                              </Typography>
                              <Box display="flex" gap={1} flexWrap="wrap">
                                {condition.value.map((option, chipIndex) => (
                                  <Chip
                                    key={chipIndex}
                                    variant="outlined"
                                    label={option}
                                    size="small"
                                    onDelete={() => {
                                      const currentValue = Array.isArray(condition.value) ? condition.value : [];
                                      const newValue = currentValue.filter((_, i) => i !== chipIndex);
                                      const newConditions = ruleData.condition?.map((c, i) => 
                                        i === index ? { ...c, value: newValue } : c
                                      ) || [];
                                      updateRuleData({ condition: newConditions });
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </>
                      ) : (condition.operator === 'equal' || condition.operator === 'notEqual') ? (
                        <Autocomplete
                          options={onboardedRegions}
                          value={typeof condition.value === 'string' ? condition.value : ''}
                          onChange={(event, newValue) => {
                            const newConditions = ruleData.condition?.map((c, i) => 
                              i === index ? { ...c, value: newValue || '' } : c
                            ) || [];
                            updateRuleData({ condition: newConditions });
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Region"
                              placeholder="e.g., us-east-1"
                              helperText="Select the region to match"
                            />
                          )}
                        />
                      ) : (
                        <TextField
                          fullWidth
                          label="Region"
                          value={typeof condition.value === 'string' ? condition.value : ''}
                          onChange={(e) => {
                            const newConditions = ruleData.condition?.map((c, i) => 
                              i === index ? { ...c, value: e.target.value } : c
                            ) || [];
                            updateRuleData({ condition: newConditions });
                          }}
                          placeholder="e.g., us-east-1"
                          helperText="Enter the region to match"
                        />
                      )}
                    </>
                  )}

                  {condition.type === 'accountId' && (
                    <>
                      {(condition.operator === 'isInOrganization' || condition.operator === 'isNotInOrganization') ? (
                        <>
                          <Autocomplete
                            options={organizations}
                            getOptionLabel={(option) => option.organization_name}
                            value={organizations.find(org => org.organizationManagementAccountId === condition.value) || null}
                            onChange={(event, newValue) => {
                              const organizationId = newValue ? newValue.organizationManagementAccountId : '';
                              const newConditions = ruleData.condition?.map((c, i) => 
                                i === index ? { ...c, value: organizationId } : c
                              ) || [];
                              updateRuleData({ condition: newConditions });
                            }}
                            loading={loadingOrganizations}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Organization"
                                placeholder="Select organization"
                                helperText="Select the organization to check membership"
                                error={!condition.value}
                              />
                            )}
                          />
                        </>
                      ) : (condition.operator === 'isIn' || condition.operator === 'isNotIn') ? (
                        <>
                          <Autocomplete
                            multiple
                            options={onboardedAccounts}
                            getOptionLabel={(option) => `${option.account_name} (${option.accountId})`}
                            value={onboardedAccounts.filter(acc => 
                              Array.isArray(condition.value) && condition.value.includes(acc.accountId)
                            )}
                            onChange={(event, newValue) => {
                              const accountIds = newValue.map(acc => acc.accountId);
                              const newConditions = ruleData.condition?.map((c, i) => 
                                i === index ? { ...c, value: accountIds } : c
                              ) || [];
                              updateRuleData({ condition: newConditions });
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Account IDs"
                                placeholder="Select accounts"
                              />
                            )}
                            renderTags={() => null} // Don't render tags inline
                            disableCloseOnSelect
                          />
                          {Array.isArray(condition.value) && condition.value.length > 0 && (
                            <Box mt={1}>
                              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                Selected Account IDs:
                              </Typography>
                              <Box display="flex" gap={1} flexWrap="wrap">
                                {condition.value.map((accountId, chipIndex) => {
                                  const account = onboardedAccounts.find(acc => acc.accountId === accountId);
                                  const displayLabel = account ? `${account.account_name} (${accountId})` : accountId;
                                  return (
                                    <Chip
                                      key={chipIndex}
                                      variant="outlined"
                                      label={displayLabel}
                                      size="small"
                                      onDelete={() => {
                                        const currentValue = Array.isArray(condition.value) ? condition.value : [];
                                        const newValue = currentValue.filter((_, i) => i !== chipIndex);
                                        const newConditions = ruleData.condition?.map((c, i) => 
                                          i === index ? { ...c, value: newValue } : c
                                        ) || [];
                                        updateRuleData({ condition: newConditions });
                                      }}
                                    />
                                  );
                                })}
                              </Box>
                            </Box>
                          )}
                        </>
                      ) : (condition.operator === 'equal' || condition.operator === 'notEqual') ? (
                        <Autocomplete
                          options={onboardedAccounts}
                          getOptionLabel={(option) => `${option.account_name} (${option.accountId})`}
                          value={onboardedAccounts.find(acc => acc.accountId === condition.value) || null}
                          onChange={(event, newValue) => {
                            const accountId = newValue ? newValue.accountId : '';
                            const newConditions = ruleData.condition?.map((c, i) => 
                              i === index ? { ...c, value: accountId } : c
                            ) || [];
                            updateRuleData({ condition: newConditions });
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Account ID"
                              placeholder="e.g., 123456789012"
                              helperText="Select the account ID to match"
                            />
                          )}
                        />
                      ) : (
                        <TextField
                          fullWidth
                          label="Account ID"
                          value={typeof condition.value === 'string' ? condition.value : ''}
                          onChange={(e) => {
                            const newConditions = ruleData.condition?.map((c, i) => 
                              i === index ? { ...c, value: e.target.value } : c
                            ) || [];
                            updateRuleData({ condition: newConditions });
                          }}
                          placeholder="e.g., 123456789012"
                          helperText="Enter the account ID to match"
                        />
                      )}
                    </>
                  )}

                  {condition.type === 'createdTime' && (
                    <>
                      {(() => {
                        const ep = parseCreationEpochValue(condition.value);
                        const displayValue =
                          Number.isNaN(ep) || ep <= 0 ? '' : format(new Date(ep * 1000), 'PPpp');
                        const helperText =
                          Number.isNaN(ep) || ep <= 0
                            ? 'Choose date, hour, and minute (stored as epoch seconds for the API)'
                            : `Stored cutoff: ${format(new Date(ep * 1000), 'PPpp')}`;
                        return (
                          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                            <TextField
                              fullWidth
                              label="Cutoff (local date and time)"
                              value={displayValue}
                              InputLabelProps={{ shrink: true }}
                              inputProps={{ readOnly: true }}
                              placeholder="Select cutoff date and time"
                              helperText={helperText}
                              error={hasConditionError(condition)}
                            />
                            <Button
                              variant="outlined"
                              startIcon={<CalendarMonthIcon />}
                              sx={{ mt: 0.5, minWidth: 120 }}
                              onClick={() => openCreatedTimePicker(index, condition.value)}
                            >
                              Select
                            </Button>
                          </Box>
                        );
                      })()}
                      <Box sx={{ mt: 1 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={
                                Array.isArray(condition.flags) &&
                                condition.flags.includes(ALLOW_UNKNOWN_CREATION_TIME_FLAG)
                              }
                              disabled={
                                Boolean(ruleNameUsingAllowUnknownCreationTime) &&
                                !(
                                  Array.isArray(condition.flags) &&
                                  condition.flags.includes(ALLOW_UNKNOWN_CREATION_TIME_FLAG)
                                )
                              }
                              onChange={(e) => {
                                const on = e.target.checked;
                                const newConditions =
                                  ruleData.condition?.map((c, i) =>
                                    i === index
                                      ? {
                                          ...c,
                                          flags: on ? [ALLOW_UNKNOWN_CREATION_TIME_FLAG] : undefined,
                                        }
                                      : c
                                  ) || [];
                                updateRuleData({ condition: newConditions });
                              }}
                            />
                          }
                          label="Allow resources with unknown creation time for this rule"
                        />
                        {ruleNameUsingAllowUnknownCreationTime != null &&
                          !(
                            Array.isArray(condition.flags) &&
                            condition.flags.includes(ALLOW_UNKNOWN_CREATION_TIME_FLAG)
                          ) && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                              {`In use by rule "${ruleNameUsingAllowUnknownCreationTime}". Disable it there to enable this option on this rule.`}
                            </Alert>
                          )}
                      </Box>
                    </>
                  )}


                  {condition.type === 'name' && (
                    <>
                      {(condition.operator === 'isIn' || condition.operator === 'isNotIn') ? (
                        <>
                          <Autocomplete
                            multiple
                            freeSolo
                            options={[]}
                            value={Array.isArray(condition.value) ? condition.value : []}
                            onChange={(event, newValue) => {
                              const newConditions = ruleData.condition?.map((c, i) => 
                                i === index ? { ...c, value: newValue } : c
                              ) || [];
                              updateRuleData({ condition: newConditions });
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Resource Names"
                                placeholder="Enter resource names"
                                helperText="Press Enter to add each resource name"
                              />
                            )}
                            renderTags={() => null}
                            disableCloseOnSelect
                          />
                          {Array.isArray(condition.value) && condition.value.length > 0 && (
                            <Box mt={1}>
                              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                Selected Resource Names:
                              </Typography>
                              <Box display="flex" gap={1} flexWrap="wrap">
                                {condition.value.map((option, chipIndex) => (
                                  <Chip
                                    key={chipIndex}
                                    variant="outlined"
                                    label={option}
                                    size="small"
                                    onDelete={() => {
                                      const currentValue = Array.isArray(condition.value) ? condition.value : [];
                                      const newValue = currentValue.filter((_, i) => i !== chipIndex);
                                      const newConditions = ruleData.condition?.map((c, i) => 
                                        i === index ? { ...c, value: newValue } : c
                                      ) || [];
                                      updateRuleData({ condition: newConditions });
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </>
                      ) : (
                        <TextField
                          fullWidth
                          label="Resource Name"
                          value={typeof condition.value === 'string' ? condition.value : ''}
                          onChange={(e) => {
                            const newConditions = ruleData.condition?.map((c, i) => 
                              i === index ? { ...c, value: e.target.value } : c
                            ) || [];
                            updateRuleData({ condition: newConditions });
                          }}
                          placeholder="e.g., my-ec2-instance"
                          helperText={hasConditionError(condition) ? "This field is required" : "Enter the resource name to match"}
                          error={hasConditionError(condition)}
                        />
                      )}
                    </>
                  )}

                  {condition.type === 'serviceResourceType' && (
                    <>
                      {(condition.operator === 'isIn' || condition.operator === 'isNotIn') ? (
                        <>
                          <Autocomplete
                            multiple
                            freeSolo
                            options={getServiceTypeOptions}
                            value={Array.isArray(condition.value) ? condition.value : []}
                            onChange={(event, newValue) => {
                              const newConditions = ruleData.condition?.map((c, i) => 
                                i === index ? { ...c, value: newValue } : c
                              ) || [];
                              updateRuleData({ condition: newConditions });
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Services"
                                placeholder="Select or enter service names"
                                helperText="Select from dropdown or press Enter to add custom service names (e.g., ec2, s3, rds)"
                              />
                            )}
                            renderTags={() => null}
                            disableCloseOnSelect
                          />
                          {Array.isArray(condition.value) && condition.value.length > 0 && (
                            <Box mt={1}>
                              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                Selected Services:
                              </Typography>
                              <Box display="flex" gap={1} flexWrap="wrap">
                                {condition.value.map((option, chipIndex) => (
                                  <Chip
                                    key={chipIndex}
                                    variant="outlined"
                                    label={option}
                                    size="small"
                                    onDelete={() => {
                                      const currentValue = Array.isArray(condition.value) ? condition.value : [];
                                      const newValue = currentValue.filter((_, i) => i !== chipIndex);
                                      const newConditions = ruleData.condition?.map((c, i) => 
                                        i === index ? { ...c, value: newValue } : c
                                      ) || [];
                                      updateRuleData({ condition: newConditions });
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </>
                      ) : (condition.operator === 'equal' || condition.operator === 'notEqual') ? (
                        <Autocomplete
                          options={getServiceTypeOptions}
                          value={typeof condition.value === 'string' ? condition.value : ''}
                          onChange={(event, newValue) => {
                            const newConditions = ruleData.condition?.map((c, i) => 
                              i === index ? { ...c, value: newValue || '' } : c
                            ) || [];
                            updateRuleData({ condition: newConditions });
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Service"
                              placeholder="Select a service"
                              helperText="Select from the dropdown menu (e.g., ec2, s3, rds)"
                            />
                          )}
                        />
                      ) : (
                        <TextField
                          fullWidth
                          label="Service"
                          value={typeof condition.value === 'string' ? condition.value : ''}
                          onChange={(e) => {
                            const newConditions = ruleData.condition?.map((c, i) => 
                              i === index ? { ...c, value: e.target.value } : c
                            ) || [];
                            updateRuleData({ condition: newConditions });
                          }}
                          placeholder="e.g., ec2"
                          helperText="Enter service name (e.g., ec2, s3, rds)"
                        />
                      )}
                    </>
                  )}

                  {condition.type === 'tagKey' && (
                    <>
                      {(condition.operator === 'isIn' || condition.operator === 'isNotIn') ? (
                        <>
                          <Autocomplete
                            multiple
                            freeSolo
                            options={[]}
                            value={Array.isArray(condition.value) ? condition.value : []}
                            onChange={(event, newValue) => {
                              const newConditions = ruleData.condition?.map((c, i) => 
                                i === index ? { ...c, value: newValue } : c
                              ) || [];
                              updateRuleData({ condition: newConditions });
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Tag Keys"
                                placeholder="Enter tag keys"
                                helperText="Press Enter to add each tag key"
                              />
                            )}
                            renderTags={() => null}
                            disableCloseOnSelect
                          />
                          {Array.isArray(condition.value) && condition.value.length > 0 && (
                            <Box mt={1}>
                              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                Selected Tag Keys:
                              </Typography>
                              <Box display="flex" gap={1} flexWrap="wrap">
                                {condition.value.map((option, chipIndex) => (
                                  <Chip
                                    key={chipIndex}
                                    variant="outlined"
                                    label={option}
                                    size="small"
                                    onDelete={() => {
                                      const currentValue = Array.isArray(condition.value) ? condition.value : [];
                                      const newValue = currentValue.filter((_, i) => i !== chipIndex);
                                      const newConditions = ruleData.condition?.map((c, i) => 
                                        i === index ? { ...c, value: newValue } : c
                                      ) || [];
                                      updateRuleData({ condition: newConditions });
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </>
                      ) : (condition.operator === 'equal' || condition.operator === 'notEqual') ? (
                        <TextField
                          fullWidth
                          label="Tag Key"
                          value={typeof condition.value === 'string' ? condition.value : ''}
                          onChange={(e) => {
                            const newConditions = ruleData.condition?.map((c, i) => 
                              i === index ? { ...c, value: e.target.value } : c
                            ) || [];
                            updateRuleData({ condition: newConditions });
                          }}
                          placeholder="e.g., Environment"
                          helperText="Enter the tag key to match"
                          error={hasConditionError(condition)}
                        />
                      ) : (
                        <TextField
                          fullWidth
                          label="Tag Key"
                          value={typeof condition.value === 'string' ? condition.value : ''}
                          onChange={(e) => {
                            const newConditions = ruleData.condition?.map((c, i) => 
                              i === index ? { ...c, value: e.target.value } : c
                            ) || [];
                            updateRuleData({ condition: newConditions });
                          }}
                          placeholder="e.g., Environment"
                        />
                      )}
                    </>
                  )}
                  </Grid>
                )}
              </Grid>
            </Paper>
          ))}

                                  <Button
               startIcon={<AddIcon />}
               onClick={() => {
                 const newCondition: RuleCondition = {
                   type: 'resourceType',
                   operator: 'equal',
                   value: [],
                 };
                 const newConditions = [...(ruleData.condition || []), newCondition];
                 updateRuleData({ condition: newConditions });
               }}
               variant="outlined"
               disabled={ruleData.conditionOperation === 'single' && (ruleData.condition?.length || 0) >= 1}
             >
               Add Condition
             </Button>
        </Grid>
      </Grid>

    </Box>
  );

  const renderActionsStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Actions
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Define what tags to apply or remove when conditions are met.
      </Typography>

      {/* Validation Summary for this step */}
      {(validationErrors.tags || validationErrors.template) && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Action Issues</AlertTitle>
          <Box sx={{ ml: 2 }}>
                         {validationErrors.tags && (
               <Typography variant="body2" color="error" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                 <ErrorIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                 <strong>Tags:</strong> {validationErrors.tags.join(', ')}
               </Typography>
             )}
             {validationErrors.template && (
               <Typography variant="body2" color="error" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                 <ErrorIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                 <strong>Template:</strong> {validationErrors.template.join(', ')}
               </Typography>
             )}
          </Box>
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Action Type</InputLabel>
                         <Select
               value={ruleData.rule?.type || 'add'}
               label="Action Type"
               onChange={(e) => {
                 const newType = e.target.value as 'add' | 'template';
                 if (newType === 'template') {
                   // When switching back to template, restore tags/generalTags from the template
                   const templateName = ruleData.rule?.templateName;
                   const selectedTemplate = templateName ? templates[templateName] : undefined;
                   const parsedConstantTags = selectedTemplate?.constantTags ?? [];
                   const parsedGeneralTags = selectedTemplate?.generalTags ?? [];
                   updateRuleData({
                     rule: {
                       ...ruleData.rule,
                       type: 'template',
                       templateName: templateName || '',
                       tags: parsedConstantTags,
                       generalTags: parsedGeneralTags,
                     },
                   });
                 } else {
                   updateRuleData({
                     rule: { ...ruleData.rule, type: 'add', tags: [] },
                   });
                 }
               }}
             >
               <MenuItem value="add">Add Tags</MenuItem>
               <MenuItem value="template">Use Template</MenuItem>
             </Select>
          </FormControl>
        </Grid>

        {ruleData.rule?.type === 'template' && (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Template</InputLabel>
              <Select
                value={ruleData.rule?.templateName || ''}
                label="Template"
                onChange={(e) => {
                  const selectedTemplateName = e.target.value;
                  const selectedTemplate = templates[selectedTemplateName];
                  
                  // Parse static tags from the selected template
                  let parsedConstantTags: Array<Record<string, string>> = [];
                  if (selectedTemplate?.constantTags) {
                    parsedConstantTags = selectedTemplate.constantTags;
                  }
                  
                  // Parse dynamic tags from the selected template
                  let parsedGeneralTags: string[] = [];
                  if (selectedTemplate?.generalTags) {
                    parsedGeneralTags = selectedTemplate.generalTags;
                  }
                  
                  updateRuleData({ 
                    rule: { 
                      ...ruleData.rule, 
                      templateName: selectedTemplateName,
                      tags: parsedConstantTags,
                      generalTags: parsedGeneralTags
                    }
                  });
                }}
              >
                {Object.keys(templates).map((templateName) => (
                  <MenuItem key={templateName} value={templateName}>
                    {templateName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* Display parsed tags from selected template */}
        {ruleData.rule?.type === 'template' && ruleData.rule?.templateName && ((ruleData.rule?.tags && ruleData.rule.tags.length > 0) || (ruleData.rule?.generalTags && ruleData.rule.generalTags.length > 0)) && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, mt: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'primary.light' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TemplateIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Template Tags Preview
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                The following tags will be applied when this rule is executed:
              </Typography>
              
              {/* Static Tags Section */}
              {ruleData.rule?.tags && ruleData.rule.tags.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                    Static Tags ({ruleData.rule.tags.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {(ruleData.rule.tags as Array<Record<string, string>>).map((tag, index) => {
                      const tagKey = Object.keys(tag)[0];
                      const tagValue = Object.values(tag)[0];
                      
                      return (
                        <MuiChip
                          key={`static-${index}`}
                          label={`${tagKey}: ${tagValue}`}
                          color="primary"
                          variant="outlined"
                          size="small"
                          icon={<LabelIcon />}
                          sx={{ 
                            fontWeight: 500,
                            '& .MuiChip-label': {
                              fontFamily: 'monospace',
                              fontSize: '0.875rem'
                            }
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
              
              {/* Dynamic Tags Section */}
              {ruleData.rule?.generalTags && ruleData.rule.generalTags.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'secondary.main' }}>
                    Dynamic Tags ({ruleData.rule.generalTags.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {ruleData.rule.generalTags.map((tagKey, index) => (
                      <MuiChip
                        key={`dynamic-${index}`}
                        label={`${tagKey}: [Dynamic Value]`}
                        color="secondary"
                        variant="outlined"
                        size="small"
                        icon={<AutoFixHighIcon />}
                        sx={{ 
                          fontWeight: 500,
                          '& .MuiChip-label': {
                            fontFamily: 'monospace',
                            fontSize: '0.875rem'
                          }
                        }}
                      />
                    ))}
                  </Box>
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      Dynamic tags will be populated with values based on resource metadata at execution time.
                    </Typography>
                  </Alert>
                </Box>
              )}
              
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>
                    {((ruleData.rule?.tags?.length || 0) + (ruleData.rule?.generalTags?.length || 0))}
                  </strong> tag{((ruleData.rule?.tags?.length || 0) + (ruleData.rule?.generalTags?.length || 0)) !== 1 ? 's' : ''} from template <strong>"{ruleData.rule.templateName}"</strong> will be applied to matching resources.
                </Typography>
              </Alert>
            </Paper>
          </Grid>
        )}

                 {ruleData.rule?.type === 'add' && (
           <Grid item xs={12}>
             <Typography variant="subtitle2" gutterBottom>
               Tags to Add
             </Typography>
            
            {(ruleData.rule?.tags as Array<Record<string, string>>)?.map((tag, index) => {
              const tagKey = Object.keys(tag)[0] || '';
              const tagValue = Object.values(tag)[0] || '';
              const tagErrors = tagValidationErrors[`tag_${index}`] || [];
              const hasErrors = tagErrors.length > 0;
              
              return (
                <Paper key={index} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: hasErrors ? 'error.main' : 'divider' }}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <TextField
                      label="Key"
                      value={tagKey}
                      onChange={(e) => {
                        const currentTags = (ruleData.rule?.tags as Array<Record<string, string>>) || [];
                        const newTags = [...currentTags];
                        newTags[index] = { [e.target.value]: tagValue };
                        updateRuleData({ rule: { ...ruleData.rule, tags: newTags } });
                        validateIndividualTag(e.target.value, tagValue, index);
                        checkDuplicateTag(e.target.value, tagValue, index);
                      }}
                      onBlur={() => {
                        validateIndividualTag(tagKey, tagValue, index);
                        checkDuplicateTag(tagKey, tagValue, index);
                      }}
                      sx={{ flex: 1 }}
                      placeholder="e.g., Environment"
                      error={hasErrors}
                      helperText={hasErrors ? tagErrors.join(', ') : ''}
                    />
                    <TextField
                      label="Value"
                      value={tagValue}
                      onChange={(e) => {
                        const currentTags = (ruleData.rule?.tags as Array<Record<string, string>>) || [];
                        const newTags = [...currentTags];
                        newTags[index] = { [tagKey]: e.target.value };
                        updateRuleData({ rule: { ...ruleData.rule, tags: newTags } });
                        validateIndividualTag(tagKey, e.target.value, index);
                        checkDuplicateTag(tagKey, e.target.value, index);
                      }}
                      onBlur={() => {
                        validateIndividualTag(tagKey, tagValue, index);
                        checkDuplicateTag(tagKey, tagValue, index);
                      }}
                      sx={{ flex: 1 }}
                      placeholder="e.g., Production"
                      error={hasErrors}
                      helperText={hasErrors ? tagErrors.join(', ') : ''}
                    />
                    <IconButton
                      onClick={() => {
                        const currentTags = (ruleData.rule?.tags as Array<Record<string, string>>) || [];
                        const newTags = currentTags.filter((_, i) => i !== index);
                        updateRuleData({ rule: { ...ruleData.rule, tags: newTags } });
                        setTagValidationErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors[`tag_${index}`];
                          return newErrors;
                        });
                        setDuplicateTagWarnings(prev => {
                          const newWarnings = { ...prev };
                          delete newWarnings[index];
                          return newWarnings;
                        });
                        setSimilarTagWarnings(prev => {
                          const newWarnings = { ...prev };
                          delete newWarnings[index];
                          return newWarnings;
                        });
                      }}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Paper>
              );
            })}
            
            <Button
              startIcon={<AddIcon />}
              onClick={() => {
                const currentTags = (ruleData.rule?.tags as Array<Record<string, string>>) || [];
                const newTags = [...currentTags, { '': '' }];
                updateRuleData({ rule: { ...ruleData.rule, tags: newTags } });
              }}
              variant="outlined"
            >
              Add Tag
            </Button>

            {/* Display duplicate tag warnings */}
            {Object.keys(duplicateTagWarnings).length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Duplicate tag detected
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  The same tag key-value combination already exists in the following rule(s):
                </Typography>
                <Stack spacing={1}>
                  {Object.entries(duplicateTagWarnings).map(([tagIndex, ruleNames]) => {
                    const idx = parseInt(tagIndex);
                    const currentTags = (ruleData.rule?.tags as Array<Record<string, string>>) || [];
                    const tag = currentTags[idx];
                    const tagKey = tag ? Object.keys(tag)[0] : '';
                    const tagValue = tag ? Object.values(tag)[0] : '';
                    return (
                      <Box key={tagIndex}>
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                          {tagKey}={tagValue || '(empty)'}:
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {ruleNames.map(ruleName => (
                            <Button
                              key={ruleName}
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              onClick={() => {
                                onEditOtherRule?.(ruleName);
                              }}
                              sx={{ textTransform: 'none' }}
                            >
                              Edit &quot;{ruleName}&quot;
                            </Button>
                          ))}
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </Alert>
            )}

            {/* Display similar tag warnings (e.g. Costcenter vs CostCenter) */}
            {Object.keys(similarTagWarnings).length > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Similar tag key detected
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  A similar tag key (different casing) already exists in another rule. AWS tag keys are case-sensitive, so these may be unintentional duplicates.
                </Typography>
                <Stack spacing={1}>
                  {Object.entries(similarTagWarnings).map(([tagIndex, similarList]) => {
                    const idx = parseInt(tagIndex);
                    const currentTags = (ruleData.rule?.tags as Array<Record<string, string>>) || [];
                    const tag = currentTags[idx];
                    const tagKey = tag ? Object.keys(tag)[0] : '';
                    const uniqueByRule = Array.from(
                      new Map(similarList.map(s => [s.ruleName, s])).values()
                    );
                    return (
                      <Box key={tagIndex}>
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                          Your &quot;{tagKey}&quot; is similar to:
                        </Typography>
                        {uniqueByRule.map(({ ruleName, existingKey }) => (
                          <Box key={`${ruleName}-${existingKey}`} display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 0.5 }}>
                            <Typography variant="body2">
                              &quot;{existingKey}&quot; in rule &quot;{ruleName}&quot;
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              onClick={() => onEditOtherRule?.(ruleName)}
                              sx={{ textTransform: 'none' }}
                            >
                              Edit rule
                            </Button>
                          </Box>
                        ))}
                      </Box>
                    );
                  })}
                </Stack>
              </Alert>
            )}

            {/* Display blacklisted tag error */}
            {blacklistedTagError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {blacklistedTagError}
              </Alert>
            )}

            {/* Display tag validation errors */}
            {Object.keys(tagValidationErrors).length > 0 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Tag Validation Errors:
                </Typography>
                {Object.entries(tagValidationErrors).map(([tagIndex, errors]) => (
                  <Box key={tagIndex} sx={{ mb: 1 }}>
                    <Typography variant="body2" color="error">
                      Tag {parseInt(tagIndex.split('_')[1]) + 1}: {errors.join(', ')}
                    </Typography>
                  </Box>
                ))}
              </Alert>
            )}
          </Grid>
        )}
      </Grid>

    </Box>
  );

  const renderAdvancedSettingsStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Advanced Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Configure additional settings for your rule.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            Rule Status
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Enable or disable this rule.
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={ruleData.enabled !== false}
                onChange={(e) => updateRuleData({ enabled: e.target.checked })}
              />
            }
            label="Enable Rule"
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderReviewStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review & Save
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Review your rule configuration before saving.
      </Typography>

      {/* Validation Summary Card */}
      <Grid container spacing={3} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <Card 
            sx={{ 
              border: '1px solid',
              borderColor: !isValid ? 'error.main' : 'success.main',
              bgcolor: 'background.paper'
            }}
          >
            <CardHeader
              title={
                                 <Box display="flex" alignItems="center" gap={1}>
                   {!isValid ? <ErrorIcon sx={{ color: 'text.secondary' }} /> : <CheckCircleIcon color="success" />}
                   <Typography variant="h6">
                     {!isValid ? 'Validation Issues Found' : 'All Validations Passed'}
                   </Typography>
                 </Box>
              }
              action={
                <Chip
                  label={!isValid ? 'Needs Fixes' : 'Ready to Save'}
                  color={!isValid ? 'error' : 'success'}
                  size="small"
                />
              }
            />
            <CardContent>
              {!isValid ? (
                <Box>
                  <Typography variant="body2" color="error" gutterBottom>
                    The following validation issues need to be resolved:
                  </Typography>
                  
                  {/* Show specific error details */}
                  <Box sx={{ mt: 2, mb: 2 }}>
                    {validationErrors.name && (
                      <Alert severity="error" sx={{ mb: 1 }}>
                        <Typography variant="body2">
                          <strong>Basic Information:</strong> {validationErrors.name.join(', ')}
                        </Typography>
                      </Alert>
                    )}
                    {validationErrors.category && (
                      <Alert severity="error" sx={{ mb: 1 }}>
                        <Typography variant="body2">
                          <strong>Category:</strong> {validationErrors.category.join(', ')}
                        </Typography>
                      </Alert>
                    )}
                    {validationErrors.conditions && (
                      <Alert severity="error" sx={{ mb: 1 }}>
                        <Typography variant="body2">
                          <strong>Conditions:</strong> {validationErrors.conditions.join(', ')}
                        </Typography>
                      </Alert>
                    )}
                    {validationErrors.tags && (
                      <Alert severity="error" sx={{ mb: 1 }}>
                        <Typography variant="body2">
                          <strong>Tags:</strong> {validationErrors.tags.join(', ')}
                        </Typography>
                      </Alert>
                    )}
                    {validationErrors.template && (
                      <Alert severity="error" sx={{ mb: 1 }}>
                        <Typography variant="body2">
                          <strong>Template:</strong> {validationErrors.template.join(', ')}
                        </Typography>
                      </Alert>
                    )}
                  </Box>
                  
                  <Typography variant="body2" color="error" gutterBottom>
                    Click the buttons below to navigate to the steps that need fixes:
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                    {(validationErrors.name || validationErrors.category) && (
                                           <Button
                       size="small"
                       variant="outlined"
                       color="error"
                       onClick={() => setActiveStep(0)}
                       startIcon={<ErrorIcon />}
                     >
                       Fix Basic Info
                     </Button>
                   )}
                   {validationErrors.conditions && (
                     <Button
                       size="small"
                       variant="outlined"
                       color="error"
                       onClick={() => setActiveStep(1)}
                       startIcon={<ErrorIcon />}
                     >
                       Fix Conditions
                     </Button>
                   )}
                   {(validationErrors.tags || validationErrors.template) && (
                     <Button
                       size="small"
                       variant="outlined"
                       color="error"
                       onClick={() => setActiveStep(2)}
                       startIcon={<ErrorIcon />}
                     >
                       Fix Actions
                     </Button>
                   )}
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color="success.main">
                  ✓ All required fields are filled correctly. You can now save your rule.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Duplicate / Similar tag warnings (proceeded despite warnings) */}
        {(Object.keys(duplicateTagWarnings).length > 0 || Object.keys(similarTagWarnings).length > 0) && (
          <Grid item xs={12}>
            {Object.keys(duplicateTagWarnings).length > 0 && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Duplicate tag detected
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  The same tag key-value combination already exists in the following rule(s):
                </Typography>
                <Stack spacing={1}>
                  {Object.entries(duplicateTagWarnings).map(([tagIndex, ruleNames]) => {
                    const idx = parseInt(tagIndex);
                    const currentTags = (ruleData.rule?.tags as Array<Record<string, string>>) || [];
                    const tag = currentTags[idx];
                    const tagKey = tag ? Object.keys(tag)[0] : '';
                    const tagValue = tag ? Object.values(tag)[0] : '';
                    return (
                      <Box key={tagIndex}>
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                          {tagKey}={tagValue || '(empty)'}:
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {ruleNames.map(ruleName => (
                            <Button
                              key={ruleName}
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              onClick={() => onEditOtherRule?.(ruleName)}
                              sx={{ textTransform: 'none' }}
                            >
                              Edit &quot;{ruleName}&quot;
                            </Button>
                          ))}
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </Alert>
            )}
            {Object.keys(similarTagWarnings).length > 0 && (
              <Alert severity="info">
                <Typography variant="subtitle2" gutterBottom>
                  Similar tag key detected
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  A similar tag key (different casing) already exists in another rule. AWS tag keys are case-sensitive, so these may be unintentional duplicates.
                </Typography>
                <Stack spacing={1}>
                  {Object.entries(similarTagWarnings).map(([tagIndex, similarList]) => {
                    const idx = parseInt(tagIndex);
                    const currentTags = (ruleData.rule?.tags as Array<Record<string, string>>) || [];
                    const tag = currentTags[idx];
                    const tagKey = tag ? Object.keys(tag)[0] : '';
                    const uniqueByRule = Array.from(
                      new Map(similarList.map(s => [s.ruleName, s])).values()
                    );
                    return (
                      <Box key={tagIndex}>
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                          Your &quot;{tagKey}&quot; is similar to:
                        </Typography>
                        {uniqueByRule.map(({ ruleName, existingKey }) => (
                          <Box key={`${ruleName}-${existingKey}`} display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 0.5 }}>
                            <Typography variant="body2">
                              &quot;{existingKey}&quot; in rule &quot;{ruleName}&quot;
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              onClick={() => onEditOtherRule?.(ruleName)}
                              sx={{ textTransform: 'none' }}
                            >
                              Edit rule
                            </Button>
                          </Box>
                        ))}
                      </Box>
                    );
                  })}
                </Stack>
              </Alert>
            )}
          </Grid>
        )}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Rule Summary"
              action={
                <Chip
                  label="Active"
                  color="success"
                  size="small"
                />
              }
            />
            <CardContent>
              <Grid container spacing={2}>
                                                                   <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Name
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {ruleData.name || 'No name provided'}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Category
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {RULE_CATEGORIES[ruleData.category as keyof typeof RULE_CATEGORIES]?.name || 'Uncategorized'}
                    </Typography>
                  </Grid>
                
                 <Grid item xs={12}>
                   <Typography variant="subtitle2" color="text.secondary">
                     Description
                   </Typography>
                   <Typography variant="body1" gutterBottom>
                     {ruleData.description || 'No description provided'}
                   </Typography>
                 </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Conditions
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {ruleData.condition?.map((condition, index) => (
                      <Chip
                        key={index}
                        label={
                          condition.type === 'createdTime'
                            ? (() => {
                                const ep = parseCreationEpochValue(condition.value);
                                const human =
                                  !Number.isNaN(ep) && ep > 0
                                    ? format(new Date(ep * 1000), 'PPpp')
                                    : String(condition.value ?? '');
                                const unk =
                                  Array.isArray(condition.flags) &&
                                  condition.flags.includes(ALLOW_UNKNOWN_CREATION_TIME_FLAG)
                                    ? ' (allow unknown creation time)'
                                    : '';
                                return `Created Time ${condition.operator} ${human}${unk}`;
                              })()
                            : `${condition.type} ${condition.operator} ${Array.isArray(condition.value) ? condition.value.join(', ') : condition.value}`
                        }
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Grid>
                
                                 <Grid item xs={12}>
                   <Typography variant="subtitle2" color="text.secondary">
                     Actions
                   </Typography>
                   <Typography variant="body1" gutterBottom>
                     {ruleData.rule?.type === 'template' 
                       ? `Apply template: ${ruleData.rule.templateName}`
                       : `Add ${(ruleData.rule?.tags as Array<Record<string, string>>)?.length || 0} tag(s)`
                     }
                   </Typography>
                 </Grid>
                 
                 <Grid item xs={12}>
                   <Typography variant="subtitle2" color="text.secondary">
                     Status
                   </Typography>
                   <Typography variant="body1" gutterBottom>
                     {ruleData.enabled !== false ? 'Enabled' : 'Disabled'}
                   </Typography>
                 </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

    </Box>
  );

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return renderBasicInformationStep();
      case 1:
        return renderConditionsStep();
      case 2:
        return renderActionsStep();
      case 3:
        return renderAdvancedSettingsStep();
      case 4:
        return renderReviewStep();
      default:
        return null;
    }
  };

  const createdTimeCalendarDays = useMemo(
    () => buildCalendarDays(createdTimePickerMonth),
    [createdTimePickerMonth]
  );
  const createdTimeYearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years: number[] = [];
    for (let year = currentYear - 15; year <= currentYear + 15; year += 1) {
      years.push(year);
    }
    return years;
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <SettingsIcon color="primary" />
            <Typography variant="h6">
              {existingRule ? 'Edit Rule' : 'Create New Rule'}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <StepIndicator 
          steps={steps} 
          onStepClick={(stepId) => {
            const stepIndex = steps.findIndex(s => s.id === stepId);
            if (stepIndex !== -1) {
              setActiveStep(stepIndex);
            }
          }} 
        />

        <Box sx={{ p: 3 }}>
          <motion.div
            key={activeStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {renderStepContent(activeStep)}
          </motion.div>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Box display="flex" justifyContent="space-between" width="100%">
          <Button
            onClick={handleBack}
            disabled={activeStep === 0}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
          
          <Box display="flex" gap={2}>
            <Button onClick={onClose} startIcon={<CancelIcon />}>
              Cancel
            </Button>
            
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!isValid || saving}
                startIcon={saving ? <HourglassEmptyIcon /> : <SaveIcon />}
              >
                {saving ? 'Saving...' : 'Save Rule'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<ArrowForwardIcon />}
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      </DialogActions>

      <Dialog
        open={createdTimePickerIndex != null}
        onClose={closeCreatedTimePicker}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Select cutoff date and time
          </Typography>
          <Typography variant="body2" color="text.secondary">
            TagOps calendar selector
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <IconButton
              size="small"
              onClick={() => setCreatedTimePickerMonth(prev => subMonths(prev, 1))}
              aria-label="Previous month"
            >
              <NavigateBeforeIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel id="created-time-month-label">Month</InputLabel>
                <Select
                  labelId="created-time-month-label"
                  label="Month"
                  value={String(createdTimePickerMonth.getMonth())}
                  onChange={(e) => {
                    const month = parseInt(e.target.value as string, 10);
                    setCreatedTimePickerMonth(prev => {
                      const next = new Date(prev);
                      next.setMonth(month, 1);
                      return startOfMonth(next);
                    });
                  }}
                >
                  {Array.from({ length: 12 }).map((_, monthIndex) => (
                    <MenuItem key={monthIndex} value={String(monthIndex)}>
                      {format(new Date(2026, monthIndex, 1), 'MMMM')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel id="created-time-year-label">Year</InputLabel>
                <Select
                  labelId="created-time-year-label"
                  label="Year"
                  value={String(createdTimePickerMonth.getFullYear())}
                  onChange={(e) => {
                    const year = parseInt(e.target.value as string, 10);
                    setCreatedTimePickerMonth(prev => {
                      const next = new Date(prev);
                      next.setFullYear(year);
                      return startOfMonth(next);
                    });
                  }}
                >
                  {createdTimeYearOptions.map(year => (
                    <MenuItem key={year} value={String(year)}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <IconButton
              size="small"
              onClick={() => setCreatedTimePickerMonth(prev => addMonths(prev, 1))}
              aria-label="Next month"
            >
              <NavigateNextIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
              gap: 0.5,
              mb: 0.5,
            }}
          >
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(dayLabel => (
              <Typography
                key={dayLabel}
                variant="caption"
                color="text.secondary"
                align="center"
                sx={{ display: 'block', fontWeight: 600 }}
              >
                {dayLabel}
              </Typography>
            ))}
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
              gap: 0.5,
            }}
          >
            {createdTimeCalendarDays.map(day => {
              const isSelected = isSameDay(day, createdTimePickerValue);
              const inMonth = isSameMonth(day, createdTimePickerMonth);
              return (
                <Button
                  key={day.toISOString()}
                  onClick={() =>
                    setCreatedTimePickerValue(prev => {
                      const next = new Date(day);
                      next.setHours(prev.getHours(), prev.getMinutes(), 0, 0);
                      return next;
                    })
                  }
                  variant={isSelected ? 'contained' : 'text'}
                  size="small"
                  sx={{
                    minWidth: 0,
                    width: '100%',
                    py: 0.75,
                    borderRadius: 1.5,
                    opacity: inMonth ? 1 : 0.45,
                    fontWeight: isSelected ? 600 : 500,
                  }}
                >
                  {format(day, 'd')}
                </Button>
              );
            })}
          </Box>

          <Box sx={{ mt: 2.5, display: 'flex', gap: 1.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="created-time-hour-label">Hour</InputLabel>
              <Select
                labelId="created-time-hour-label"
                label="Hour"
                value={String(createdTimePickerValue.getHours())}
                onChange={(e) => {
                  const hour = parseInt(e.target.value as string, 10);
                  setCreatedTimePickerValue(prev => {
                    const next = new Date(prev);
                    next.setHours(hour);
                    return next;
                  });
                }}
              >
                {Array.from({ length: 24 }).map((_, hour) => (
                  <MenuItem key={hour} value={String(hour)}>
                    {String(hour).padStart(2, '0')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel id="created-time-minute-label">Minute</InputLabel>
              <Select
                labelId="created-time-minute-label"
                label="Minute"
                value={String(createdTimePickerValue.getMinutes())}
                onChange={(e) => {
                  const minute = parseInt(e.target.value as string, 10);
                  setCreatedTimePickerValue(prev => {
                    const next = new Date(prev);
                    next.setMinutes(minute);
                    return next;
                  });
                }}
              >
                {Array.from({ length: 60 }).map((_, minute) => (
                  <MenuItem key={minute} value={String(minute)}>
                    {String(minute).padStart(2, '0')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
            Selected: {format(createdTimePickerValue, 'PPpp')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeCreatedTimePicker}>Cancel</Button>
          <Button variant="contained" onClick={applyCreatedTimePicker}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Overlay */}
      {saving && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          zIndex={9999}
        >
          <LinearProgress />
        </Box>
      )}
    </Dialog>
  );
};
